import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Calendar, RefreshCw, FileText, Users, CalendarDays, Download, Package, Search, X, ChevronDown, ChevronLeft, ChevronRight, Bot, ArrowUpRight, Sparkles } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import * as XLSX from 'xlsx';
import { rowViewOf, exposureOf, fmtMoney, fmtMoneyCompact } from './shell/useAgentTickets';
import { SkeletonRows, NoResults, ErrorBanner } from './shell/carbonStates';
import CarbonBars from './carbon/CarbonBars';
import TargetProductionSalesHybrid from './TargetProductionSalesHybrid';
import CostcoProductionSalesDashboard from './CostcoProductionSalesDashboard';
import SamsProductionSalesDashboard from './SamsProductionSalesDashboard';
import ChampionProductionSalesDashboard from './ChampionProductionSalesDashboard';
import InMoceanProductionSalesDashboard from './InMoceanProductionSalesDashboard';
import WalmartProductionSalesTest from './WalmartProductionSalesTest';
import AnneColeCutAndSoldDashboard from './AnneColeCutAndSoldDashboard';
import HurleyCutAndSoldDashboard from './HurleyCutAndSoldDashboard';
import IMGApparelCutAndSoldDashboard from './IMGApparelCutAndSoldDashboard';
import TargetBalanceSheetWorkflow from './TargetBalanceSheetWorkflow';
import TargetBalanceSheetDashboard from './TargetBalanceSheetDashboard';
import PreAllocationDashboard from './PreAllocationDashboard';
import { isPageGranted } from './shell/navConfig';

// ============================================================================
// ORDERS DASHBOARD - Combined Invoice + Sales/Open Orders
// Tab 1: Invoiced (from Invoice2Dashboard)
// Tab 2: Open/Pick (from Sales2Dashboard)
// ============================================================================
// CHA-274 — Carbon pass (presentation only). Data layer, callables, filters,
// aggregation logic and exports are unchanged; the chrome follows the Orders
// concept (docs/design reskin handoff §6): Carbon tabs, per-tile summary
// strips, the reference table pattern (search + status chips + mono grid +
// pagination + drawer), severity rails/bot marks joined client-side against
// the `agentTickets` view model (fail-safe: rows render without rails when
// the model is loading or errored).
export default function OrdersDashboard({ agentTickets, tenantId, userProfile }) {
  // CHA-557 (Codex): the Target Balance Sheet moved here from Data, but its
  // grant is still `data/target`. Gate the tab on that page so a role with
  // `inmocean/orders` but no `data/target` cannot see it or invoke its
  // production-write upload. Same allow-by-default policy as the rest of C.
  const canSeeTarget = isPageGranted('data/target', userProfile?.pages);
  // Sub-toggle within the Target tab: 'upload' (workflow) | 'balance-sheet'
  // (dashboard). Both moved from the old Data/Target section; without this the
  // Balance Sheet view is unreachable (Codex P2).
  const [targetTab, setTargetTab] = useState('upload');
  // Tab state
  const [activeTab, setActiveTab] = useState('open'); // 'invoiced', 'open', 'cut-sold', 'target-balance' or 'pre-allocation'
  // CHA-652 — tabs that load their own data; the shared Orders fetch must not run for them.
  const ownsData = activeTab === 'target-balance' || activeTab === 'pre-allocation';
  const [cutSoldSubTab, setCutSoldSubTab] = useState('walmart'); // 'walmart' or 'target'
  // CHA-394 — the customer dashboards are the primary Cut & Sold surface; the season snapshot is
  // demoted to a collapsed strip. Collapsed = not mounted, so getSeasonCutAndSold only fires on expand.
  const [showSeasonSnapshot, setShowSeasonSnapshot] = useState(false);

  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Common filter state
  const [company, setCompany] = useState('All');

  // Invoice-specific filters
  const [invoiceStartDate, setInvoiceStartDate] = useState('2025-07-01');
  const [invoiceEndDate, setInvoiceEndDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Open/Pick-specific filters
  const [openEndDate, setOpenEndDate] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDay.toISOString().split('T')[0];
  });
  const [selectedCells, setSelectedCells] = useState([]);
  const [matrixView, setMatrixView] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);

  // Shipping Snapshot state
  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Yesterday's Unshipped state
  const [yesterdayData, setYesterdayData] = useState(null);
  const [yesterdayLoading, setYesterdayLoading] = useState(false);

  // Shared filters for snapshot tables
  const [snapshotStatusFilter, setSnapshotStatusFilter] = useState(null);
  const [snapshotCompanyFilter, setSnapshotCompanyFilter] = useState(null);

  // Format currency in Millions with 2 decimal places
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '$0';
    const num = Number(value) || 0;
    if (num === 0) return '$0';
    const millions = num / 1000000;
    if (Math.abs(millions) >= 0.01) {
      return `$${millions.toFixed(2)}M`;
    }
    const thousands = num / 1000;
    if (Math.abs(thousands) >= 1) {
      return `$${thousands.toFixed(0)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return '—';
    return String(dateValue).split('T')[0] || String(dateValue);
  };

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();

      if (activeTab === 'invoiced') {
        const getInvoiceData = httpsCallable(functions, 'getInvoiceData');
        const result = await getInvoiceData({
          company: company === 'All' ? null : company,
          startDate: invoiceStartDate || null,
          endDate: invoiceEndDate || null,
          selectedMonth: selectedMonth || null,
          selectedCustomer: selectedCustomer || null,
          selectedDay: selectedDay || null,
        });
        setData(result.data);
      } else if (activeTab === 'target-balance' || activeTab === 'pre-allocation') {
        setData(null);
      } else {
        const getSalesData = httpsCallable(functions, 'getSalesData');
        const result = await getSalesData({
          company: company === 'All' ? null : company,
          startDate: null,
          endDate: openEndDate || null,
          selectedCells: selectedCells.length > 0 ? selectedCells : null,
          projectedDate: selectedDate || null,
        });
        setData(result.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, company, invoiceStartDate, invoiceEndDate, selectedMonth, selectedCustomer, selectedDay, openEndDate, selectedCells, selectedDate]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch shipping snapshot data
  const fetchSnapshot = useCallback(async () => {
    setSnapshotLoading(true);
    try {
      const functions = getFunctions();
      const getShippingSnapshot = httpsCallable(functions, 'getShippingSnapshot');
      const result = await getShippingSnapshot();
      setSnapshotData(result.data);
    } catch (err) {
      console.error('Error fetching snapshot:', err);
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  // Fetch yesterday's unshipped data
  const fetchYesterdayUnshipped = useCallback(async () => {
    setYesterdayLoading(true);
    try {
      const functions = getFunctions();
      const getYesterdayUnshipped = httpsCallable(functions, 'getYesterdayUnshipped');
      const result = await getYesterdayUnshipped();
      setYesterdayData(result.data);
    } catch (err) {
      console.error('Error fetching yesterday unshipped:', err);
    } finally {
      setYesterdayLoading(false);
    }
  }, []);

  // Load snapshot and yesterday data when on open tab
  useEffect(() => {
    if (activeTab === 'open') {
      fetchSnapshot();
      fetchYesterdayUnshipped();
    }
  }, [activeTab, fetchSnapshot, fetchYesterdayUnshipped]);

  // Clear all filters based on tab
  const clearFilters = () => {
    setCompany('All');
    if (activeTab === 'invoiced') {
      setSelectedMonth(null);
      setSelectedCustomer(null);
      setSelectedDay(null);
      setInvoiceStartDate('2025-07-01');
      setInvoiceEndDate(new Date().toISOString().split('T')[0]);
    } else {
      setSelectedCells([]);
      setSelectedDate(null);
      setMatrixView('all');
    }
  };

  // Switch tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setData(null);
    clearFilters();
  };

  // Check if any filters are active
  const hasActiveFilters = ownsData
    ? false
    : activeTab === 'invoiced'
      ? company !== 'All' || selectedMonth !== null || selectedCustomer !== null || selectedDay !== null
      : company !== 'All' || selectedCells.length > 0;

  // Get selected month label for display
  const getSelectedMonthLabel = () => {
    if (!selectedMonth || !data?.invoiceByMonth) return '';
    const monthData = data.invoiceByMonth.find(m => m.month === selectedMonth);
    return monthData?.monthLabel || selectedMonth;
  };

  // Handle matrix cell click for Open tab
  const handleMatrixClick = (division, shipWindow) => {
    if (division === 'TOTAL') {
      setSelectedCells(prev => {
        const hasAnyInRow = prev.some(c => c.shipWindow === shipWindow);
        if (hasAnyInRow) {
          return prev.filter(c => c.shipWindow !== shipWindow);
        } else {
          const allDivisions = data?.matrix?.filter(r => r.division !== 'TOTAL').map(r => r.division) || [];
          const newCells = allDivisions.map(d => ({ division: d, shipWindow }));
          return [...prev, ...newCells];
        }
      });
    } else if (shipWindow === 'total') {
      setSelectedCells(prev => {
        const hasAnyInCol = prev.some(c => c.division === division);
        if (hasAnyInCol) {
          return prev.filter(c => c.division !== division);
        } else {
          const allShipWindows = data?.shipWindowColumns || [];
          const newCells = allShipWindows.map(sw => ({ division, shipWindow: sw }));
          return [...prev, ...newCells];
        }
      });
    } else {
      setSelectedCells(prev => {
        const exists = prev.some(c => c.division === division && c.shipWindow === shipWindow);
        if (exists) {
          return prev.filter(c => !(c.division === division && c.shipWindow === shipWindow));
        } else {
          return [...prev, { division, shipWindow }];
        }
      });
    }
  };

  // Calculate filtered summary from matrix data based on selected cells
  const getFilteredSummary = () => {
    if (!data?.matrix) return data?.summary || {};
    if (selectedCells.length === 0) return data.summary || {};

    let totalPick = 0, pickCount = 0;
    data.matrix.forEach(row => {
      if (row.division === 'TOTAL') return;
      selectedCells.forEach(cell => {
        if (cell.division === row.division) {
          const cellValue = Number(row[cell.shipWindow]) || 0;
          const cellCount = Number(row[`${cell.shipWindow}_count`]) || 0;
          totalPick += cellValue;
          pickCount += cellCount;
        }
      });
    });

    return {
      ...data.summary,
      total_pick: totalPick,
      pick_count: pickCount,
    };
  };

  const filteredSummary = getFilteredSummary();

  // CHA-274 — client-side join: open agent tickets keyed by PO. Fail-safe by
  // construction: while the model is loading (or errored) `open` is empty, so
  // rows simply render without rails — orders is never blocked on tickets.
  const openTickets = agentTickets?.open;
  const ticketsByPo = useMemo(() => {
    const map = new Map();
    for (const t of openTickets || []) {
      const key = String(t.po_no ?? '').trim();
      if (!key) continue;
      const prev = map.get(key);
      if (!prev || (!rowViewOf(prev).urgent && rowViewOf(t).urgent)) map.set(key, t);
    }
    return map;
  }, [openTickets]);

  // Per-tab count label (concept anatomy). Only the active tab's dataset is
  // loaded, so the label reflects the active tab.
  const countLabel =
    activeTab === 'invoiced'
      ? data?.invoiceDetails
        ? `${data.invoiceDetails.length} RECORDS`
        : ''
      : activeTab === 'open'
        ? data?.orderDetails
          ? `${data.orderDetails.length} ORDERS`
          : ''
        : activeTab === 'target-balance'
          ? 'TARGET UPLOADS'
          : activeTab === 'pre-allocation'
          ? 'PRE-ALLOCATION SNAPSHOT'
          : 'ALL DIVISIONS';

  return (
    <div className="space-y-5 font-plex text-carbon-ink">
      {/* Header — page title lives in the shell TopBar; here: count + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <span className="font-plex-mono text-[11px] text-carbon-gray-50">{countLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-xs font-medium text-carbon-gray-70 hover:text-carbon-ink hover:bg-carbon-gray-10 transition-colors"
            >
              Clear Filters
            </button>
          )}
          {!ownsData && (
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-3.5 py-2 text-[12.5px] font-medium bg-transparent border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation — Carbon tabs */}
      <div className="flex items-center border-b border-carbon-border">
        {[
          { key: 'open', label: 'Open / Pick' },
          { key: 'invoiced', label: 'Invoiced' },
          { key: 'cut-sold', label: 'Cut & Sold' },
          // Target Balance Sheet only for roles granted data/target (CHA-557).
          ...(canSeeTarget ? [{ key: 'target-balance', label: 'Target Balance Sheet' }] : []),
          { key: 'pre-allocation', label: 'Pre Allocation' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={`px-[18px] py-2.5 text-[13.5px] -mb-px border-b-2 transition-colors ${
              activeTab === t.key
                ? 'font-semibold text-carbon-ink border-carbon-blue-60'
                : 'font-normal text-carbon-gray-50 border-transparent hover:text-carbon-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto self-center text-[12.5px] text-carbon-gray-50">
          {activeTab === 'invoiced' ? 'Invoiced orders analysis' : activeTab === 'cut-sold' ? 'WIP, Open, Pick, Invoice by Customer, Program & Fine Line' : activeTab === 'target-balance' ? 'Target commitment uploads' : activeTab === 'pre-allocation' ? 'Latest daily allocation snapshot' : 'Open & Pick orders analysis'}
        </span>
      </div>

      {/* Error Display */}
      {error && (
        <ErrorBanner
          what="Couldn't load orders data."
          staleNote={error}
          onRetry={fetchData}
        />
      )}

      {/* ==================== INVOICED TAB CONTENT ==================== */}
      {activeTab === 'invoiced' && (
        <>
          {/* Row 1: Filters + Summary strip */}
          <div className="bg-carbon-white border border-carbon-border p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Company Filter */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => setCompany('All')} className={carbonChipCls(company === 'All')}>
                  ALL
                </button>
                {data?.companies?.map((c) => (
                  <button key={c} onClick={() => setCompany(c)} className={carbonChipCls(company === c)}>
                    {c}
                  </button>
                ))}
              </div>

              {/* Invoice Date Range */}
              <div className="flex items-center gap-2">
                <Calendar size={15} className="text-carbon-gray-50" />
                <span className="font-plex-mono text-[10px] tracking-[0.1em] uppercase text-carbon-gray-50">Invoice Date</span>
                <input
                  type="date"
                  value={invoiceStartDate}
                  onChange={(e) => setInvoiceStartDate(e.target.value)}
                  className="px-2 py-1.5 border border-carbon-border font-plex-mono text-xs focus:outline-none focus:border-carbon-blue-60"
                />
                <span className="text-xs text-carbon-gray-40">to</span>
                <input
                  type="date"
                  value={invoiceEndDate}
                  onChange={(e) => setInvoiceEndDate(e.target.value)}
                  className="px-2 py-1.5 border border-carbon-border font-plex-mono text-xs focus:outline-none focus:border-carbon-blue-60"
                />
              </div>

              <div className="ml-auto font-plex-mono text-[10.5px] text-carbon-gray-50">
                REPORT <span className="text-carbon-gray-70">{formatDate(data?.lastRptDate)}</span>
              </div>
            </div>
          </div>

          {/* Summary strip — per-tile borders (never a gray-gap grid) */}
          <CarbonStatStrip
            loading={loading}
            tiles={[
              { label: 'MTD INVOICED', value: formatCurrency(data?.summary?.mtd_invoiced), note: countNote(data?.summary?.mtd_count) },
              { label: 'TOTAL INVOICED', value: formatCurrency(data?.summary?.total_invoiced), note: countNote(data?.summary?.total_count) },
              { label: 'GROSS PROFIT', value: formatCurrency(data?.summary?.total_gross_profit), note: `${(data?.summary?.gp_percent || 0).toFixed(1)}% GP` },
              { label: 'AVG GP', value: `${(data?.summary?.gp_percent || 0).toFixed(1)}%` },
            ]}
          />

          {/* Gross by customer — Carbon ranked bars (same values as the Customer Summary table) */}
          {data?.customerSummary?.length > 0 && (
            <CarbonBars
              title="Gross by customer"
              meta={`${formatCurrency(data.customerSummary.reduce((sum, row) => sum + (row.amount || 0), 0))} TOTAL`}
              data={data.customerSummary.map((row) => ({ name: row.customerName, value: row.amount }))}
              formatValue={formatCurrency}
            />
          )}

          {/* Row 2: Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <InvoiceByMonthTable
              data={data?.invoiceByMonth}
              formatCurrency={formatCurrency}
              loading={loading}
              selectedMonth={selectedMonth}
              onRowClick={(month) => {
                setSelectedMonth(month === selectedMonth ? null : month);
                setSelectedCustomer(null);
              }}
            />
            <InvoiceCustomerSummaryTable
              data={data?.customerSummary}
              formatCurrency={formatCurrency}
              loading={loading}
              selectedMonth={selectedMonth}
              selectedMonthLabel={getSelectedMonthLabel()}
              selectedCustomer={selectedCustomer}
              onRowClick={(customer) => setSelectedCustomer(customer === selectedCustomer ? null : customer)}
            />
            <MTDByDayTable
              data={data?.mtdByDay}
              formatCurrency={formatCurrency}
              loading={loading}
              selectedDay={selectedDay}
              selectedMonth={selectedMonth}
              selectedMonthLabel={getSelectedMonthLabel()}
              onRowClick={(day) => setSelectedDay(day === selectedDay ? null : day)}
            />
          </div>

          {/* Row 3: Invoice Details */}
          <InvoiceDetailsTable
            data={data?.invoiceDetails}
            formatCurrency={formatCurrency}
            formatNumber={formatNumber}
            loading={loading}
            selectedMonth={selectedMonth}
            selectedCustomer={selectedCustomer}
            selectedDay={selectedDay}
            selectedMonthLabel={getSelectedMonthLabel()}
            ticketsByPo={ticketsByPo}
          />
        </>
      )}

      {/* ==================== OPEN/PICK TAB CONTENT ==================== */}
      {activeTab === 'open' && (
        <>
          {/* Row 1: Filters + Summary strip */}
          <div className="bg-carbon-white border border-carbon-border p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Company Filter */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => setCompany('All')} className={carbonChipCls(company === 'All')}>
                  ALL
                </button>
                {data?.companies?.map((c) => (
                  <button key={c} onClick={() => setCompany(c)} className={carbonChipCls(company === c)}>
                    {c}
                  </button>
                ))}
              </div>

              {/* End Date Filter */}
              <div className="flex items-center gap-2">
                <Calendar size={15} className="text-carbon-gray-50" />
                <span className="font-plex-mono text-[10px] tracking-[0.1em] uppercase text-carbon-gray-50">Ship by</span>
                <input
                  type="date"
                  value={openEndDate}
                  onChange={(e) => setOpenEndDate(e.target.value)}
                  className="px-2 py-1.5 border border-carbon-border font-plex-mono text-xs focus:outline-none focus:border-carbon-blue-60"
                />
              </div>

              {/* Selected Cells Display */}
              {selectedCells.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-plex-mono text-[10.5px] text-carbon-gray-50">{selectedCells.length} CELLS</span>
                  <button
                    onClick={() => setSelectedCells([])}
                    className="px-2 py-0.5 font-plex-mono text-[10.5px] border border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10"
                  >
                    Clear all ×
                  </button>
                </div>
              )}

              <div className="ml-auto font-plex-mono text-[10.5px] text-carbon-gray-50">
                REPORT <span className="text-carbon-gray-70">{formatDate(data?.lastRptDate)}</span>
              </div>
            </div>
          </div>

          {/* Summary strip — per-tile borders (never a gray-gap grid) */}
          <CarbonStatStrip
            loading={loading}
            tiles={[
              { label: 'MTD INVOICED', value: formatCurrency(filteredSummary?.mtd_invoiced), note: countNote(filteredSummary?.invoice_count) },
              { label: 'TOTAL PICK', value: formatCurrency(filteredSummary?.total_pick), note: countNote(filteredSummary?.pick_count) },
              { label: 'OPEN CONFIRMED', value: formatCurrency(filteredSummary?.open_atonce), note: countNote(filteredSummary?.open_atonce_count) },
              { label: 'OPEN BULK', value: formatCurrency(filteredSummary?.open_bulk), note: countNote(filteredSummary?.open_bulk_count) },
            ]}
          />

          {/* Row 2: Ship Window × Division Matrix */}
          <div className="bg-carbon-white border border-carbon-border overflow-hidden">
            <div className="px-[18px] py-2.5 border-b border-carbon-border flex items-center justify-between bg-carbon-gray-10">
              <div>
                <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Ship Window × Division</h3>
                <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">
                  {matrixView === 'all' ? 'Pick + Open' : matrixView === 'pick' ? 'Pick only' : matrixView === 'open' ? 'Open Confirmed' : 'Bulk only'} • Click cells to filter
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {[
                  { key: 'all', label: 'ALL' },
                  { key: 'pick', label: 'PICK' },
                  { key: 'open', label: 'OPEN' },
                  { key: 'bulk', label: 'BULK' },
                ].map((view) => (
                  <button
                    key={view.key}
                    onClick={() => setMatrixView(view.key)}
                    className={carbonChipCls(matrixView === view.key)}
                  >
                    {view.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={`overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
              {!data?.matrix ? (
                <div className="p-8 text-center text-slate-400">Loading...</div>
              ) : (
                <TransposedMatrix
                  data={data}
                  selectedCells={selectedCells}
                  onCellClick={handleMatrixClick}
                  formatCurrency={formatCurrency}
                  formatNumber={formatNumber}
                  viewMode={matrixView}
                />
              )}
            </div>
          </div>

          {/* Row 3: By Status + Routing Status */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-4">
              <OpenByStatusTable
                data={data}
                formatCurrency={formatCurrency}
                loading={loading}
                selectedCells={selectedCells}
                selectedView={matrixView}
                onRowClick={(shipWindow) => {
                  setSelectedCells(prev => {
                    const allDivisions = data?.matrix?.filter(r => r.division !== 'TOTAL').map(r => r.division) || [];
                    const selectedInRow = prev.filter(c => c.shipWindow === shipWindow);
                    const allSelected = allDivisions.every(d => selectedInRow.some(c => c.division === d));
                    if (allSelected) {
                      return prev.filter(c => c.shipWindow !== shipWindow);
                    } else {
                      const newCells = allDivisions
                        .filter(d => !prev.some(c => c.division === d && c.shipWindow === shipWindow))
                        .map(d => ({ division: d, shipWindow }));
                      return [...prev, ...newCells];
                    }
                  });
                }}
                onColumnClick={(viewType) => {
                  setMatrixView(prev => prev === viewType ? 'all' : viewType);
                }}
              />
            </div>
            <div className="lg:col-span-8">
              <RoutingStatusTable
                data={data}
                formatCurrency={formatCurrency}
                loading={loading}
                selectedCells={selectedCells}
                onRowClick={(shipWindow) => {
                  setSelectedCells(prev => {
                    const allDivisions = data?.matrix?.filter(r => r.division !== 'TOTAL').map(r => r.division) || [];
                    const selectedInRow = prev.filter(c => c.shipWindow === shipWindow);
                    const allSelected = allDivisions.every(d => selectedInRow.some(c => c.division === d));
                    if (allSelected) {
                      return prev.filter(c => c.shipWindow !== shipWindow);
                    } else {
                      const newCells = allDivisions
                        .filter(d => !prev.some(c => c.division === d && c.shipWindow === shipWindow))
                        .map(d => ({ division: d, shipWindow }));
                      return [...prev, ...newCells];
                    }
                  });
                }}
              />
            </div>
          </div>

          {/* Row 4: Scheduled By Date Chart + Order Details */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-4">
              <ScheduledByDateChart
                data={data}
                formatCurrency={formatCurrency}
                loading={loading}
                selectedCells={selectedCells}
                selectedDate={selectedDate}
                onDateClick={setSelectedDate}
              />
            </div>
            <div className="lg:col-span-8">
              <OpenOrderDetailsTable
                data={data}
                formatCurrency={formatCurrency}
                loading={loading}
                selectedCells={selectedCells}
                selectedDate={selectedDate}
                ticketsByPo={ticketsByPo}
              />
            </div>
          </div>

          {/* Row 5: Customer Summary Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <OpenCustomerSummaryTable
              title="Pick by Customer"
              data={data?.pickByCustomer}
              formatCurrency={formatCurrency}
              loading={loading}
              color="green"
            />
            <OpenCustomerSummaryTable
              title="Open Confirmed by Customer"
              data={data?.openConfirmedByCustomer}
              formatCurrency={formatCurrency}
              loading={loading}
              color="blue"
            />
            <OpenCustomerSummaryTable
              title="Open Bulk by Customer"
              data={data?.openBulkByCustomer}
              formatCurrency={formatCurrency}
              loading={loading}
              color="orange"
            />
          </div>

          {/* Row 6: Shared Filters for Snapshot Tables */}
          <SnapshotFilters
            snapshotData={snapshotData}
            yesterdayData={yesterdayData}
            statusFilter={snapshotStatusFilter}
            setStatusFilter={setSnapshotStatusFilter}
            companyFilter={snapshotCompanyFilter}
            setCompanyFilter={setSnapshotCompanyFilter}
            formatCurrency={formatCurrency}
          />

          {/* Row 7: Shipping Snapshot */}
          <ShippingSnapshotTable
            data={snapshotData}
            loading={snapshotLoading}
            formatCurrency={formatCurrency}
            onRefresh={fetchSnapshot}
            statusFilter={snapshotStatusFilter}
            companyFilter={snapshotCompanyFilter}
          />

          {/* Row 8: Yesterday's Unshipped */}
          <YesterdayUnshippedTable
            data={yesterdayData}
            loading={yesterdayLoading}
            formatCurrency={formatCurrency}
            onRefresh={fetchYesterdayUnshipped}
            statusFilter={snapshotStatusFilter}
            companyFilter={snapshotCompanyFilter}
          />
        </>
      )}

      {/* ==================== CUT & SOLD TAB CONTENT ==================== */}
      {activeTab === 'cut-sold' && (
        <div className="space-y-4">
          {/* Sub-tabs for Walmart / Target - matching main tab style */}
          <div className="bg-carbon-white border border-carbon-border">
            <div className="flex border-b border-carbon-border">
              <button
                onClick={() => setCutSoldSubTab('walmart')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'walmart'
                    ? 'text-[#0071ce] border-b-2 border-[#0071ce] bg-blue-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  {/* Walmart Spark Logo */}
                  <svg viewBox="0 0 32 32" className="h-5 w-5">
                    <g fill="#ffc220">
                      <path d="M16 3 L17.5 11 L16 14 L14.5 11 Z"/>
                      <path d="M16 29 L17.5 21 L16 18 L14.5 21 Z"/>
                      <path d="M3 16 L11 14.5 L14 16 L11 17.5 Z"/>
                      <path d="M29 16 L21 14.5 L18 16 L21 17.5 Z"/>
                      <path d="M6.5 6.5 L12 10.5 L13.5 13.5 L10.5 12 Z"/>
                      <path d="M25.5 25.5 L20 21.5 L18.5 18.5 L21.5 20 Z"/>
                      <path d="M25.5 6.5 L21.5 12 L18.5 13.5 L20 10.5 Z"/>
                      <path d="M6.5 25.5 L10.5 20 L13.5 18.5 L12 21.5 Z"/>
                    </g>
                  </svg>
                  Walmart
                </div>
              </button>
              <button
                onClick={() => setCutSoldSubTab('target')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'target'
                    ? 'text-[#cc0000] border-b-2 border-[#cc0000] bg-red-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  {/* Target Bullseye Logo */}
                  <svg viewBox="0 0 32 32" className="h-5 w-5">
                    <circle cx="16" cy="16" r="15" fill="#cc0000"/>
                    <circle cx="16" cy="16" r="10" fill="#fff"/>
                    <circle cx="16" cy="16" r="5" fill="#cc0000"/>
                  </svg>
                  Target
                </div>
              </button>
              <button
                onClick={() => setCutSoldSubTab('costco')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'costco'
                    ? 'text-[#e5001b] border-b-2 border-[#e5001b] bg-rose-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  {/* Costco Logo */}
                  <svg viewBox="0 0 120 24" className="h-5 w-auto">
                    <text x="0" y="18" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontSize="18" fill="#e5001b">COSTCO</text>
                  </svg>
                </div>
              </button>
              <button
                onClick={() => setCutSoldSubTab('sams')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'sams'
                    ? 'text-[#0066b3] border-b-2 border-[#0066b3] bg-sky-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  {/* Sam's Club Logo */}
                  <svg viewBox="0 0 100 24" className="h-5 w-auto">
                    <text x="0" y="18" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="16" fill="#0066b3">Sam's Club</text>
                  </svg>
                </div>
              </button>
              <button
                onClick={() => setCutSoldSubTab('champion')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'champion'
                    ? 'text-[#003DA5] border-b-2 border-[#003DA5] bg-indigo-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  {/* Champion Script Logo */}
                  <svg viewBox="0 65 193 45" className="h-5 w-auto" style={{ minWidth: '70px' }}>
                    <g fill="#002244" fillRule="evenodd" clipRule="evenodd">
                      <path d="M59.093 76.905c-.65 4.364-1.949 14.947.835 25.438.557 1.393 1.95 1.021 2.506.371.186-.278 2.043-2.042 2.043-2.042.649-.929 1.3-.65 1.764 0 .464.649 1.764 2.692 3.714 2.785 1.949.093 5.013-2.229 6.22-3.156.835 1.578 1.856 3.063 3.528 2.971 1.671-.094 3.249-1.3 4.642-2.785 1.021 1.207 2.135 2.506 3.435 2.414 1.3-.094 3.713-1.486 6.034-3.621.929-1.021 1.95-.557 2.6.464.649 1.021 1.579 2.692 2.786 2.692 1.205 0 2.971-1.671 3.527-2.135.557-.465 1.113-.836 1.764-.094l1.393 1.3c.836.836 2.6.65 3.898-.557 1.301-1.207 1.578-1.578 2.137-1.578.557 0 .834.743 1.391 1.3.559.557 4.828 4.271 10.492-1.485.092 2.878-.559 19.031-.559 21.353 0 1.113 1.301 1.485 3.158 1.113 1.855-.371 2.785-.928 2.691-3.62s-.465-15.968-.465-15.968c.279-2.136 2.135-5.663 4.086-5.756 1.949-.094 1.021 2.32.092 2.32-.928 0-1.949.465-1.949 1.3 0 .836.279 3.249 5.756 3.249s6.127-1.764 7.613-2.413c.928 1.764 3.713 4.549 11.418-.372 1.578 1.857 4.549 4.178 7.799 1.857 1.021-.836 2.041-2.785 1.67-4.456-.277-.65-.092-.929.371-.929h3.064c.65 0 1.764.464 2.135 2.321.373 1.856 1.672 3.435 2.693 3.342s2.971-1.671 4.084-2.785c.93 1.3 4.92 7.334 12.254-2.785.65-.928-.371-1.393-.928-.743s-2.229 1.672-3.621 1.765-3.062-1.3-4.363-3.063c-1.299-1.764-2.785-1.114-3.713.278-.65.743-1.021 1.485-1.764 2.321-.465-.836-1.021-2.6-1.949-3.25-.93-.65-1.672-.743-3.9-.186-2.227.558-4.734.836-7.24.929-1.393-.093-1.021.557-.928 1.114.092.557 1.113 2.878-.744 2.971-1.855.093-1.67-2.692-.371-4.178.65-.65-.277-2.042-1.299-1.485-1.021.557-1.672.835-2.508 2.135-.834 1.3-2.598 3.249-5.477 3.343-2.879.093-2.043-3.25-1.949-4.735 0-.835-.557-1.578-2.043-1.207-1.484.372-3.436.186-3.25 4.27-1.02 1.486-3.342 2.414-5.197 2.136-.559-.093-.744-.371-.559-1.021.188-.649.65-4.271-2.135-5.198-2.785-.929-4.92.371-6.312 1.578-.742.371-.928 0-.928-.65l.092-3.435c0-1.578-3.156-1.949-3.619-.093-.465 1.856-1.021 5.478-3.807 6.776-2.785 1.3-3.715-.371-5.199-2.228-1.486-1.857-2.971-1.764-4.643-.093l-1.67 1.578c-.65.557-1.021.371-1.578-.464-.559-.836-.93-1.95-1.951-2.043-1.02-.093-1.205.279-1.949.836l-2.135 1.671c-.836.649-1.021.649-1.857-.464 0 0-1.393-2.321-2.785-2.321-1.391 0-1.856.836-2.691 1.485 0 0-2.136 1.485-2.878 2.135-.743.65-2.878 1.95-4.92-1.113-.743-1.114-2.042-1.95-2.971-.372-.928 1.579-2.414 3.992-3.528 3.25-1.114-.743.743-2.878 2.135-3.063 1.393-.186.557-1.95-.835-2.043-1.393-.093-3.621.836-4.642 2.043-1.021 1.206-2.692 2.6-4.363 2.506-1.671-.093-2.042-1.393-2.878-3.156s-2.321-1.578-3.435-.649c-1.114.928-1.393 1.207-2.228 2.228-.835 1.021-.835.465-.928-.186s-.65-12.625.278-20.331c.371-1.951-3.621-2.601-4.457.369z"/>
                      <path d="M146.916 91.213a2.888 2.888 0 1 0 .001-5.777 2.888 2.888 0 0 0-.001 5.777z"/>
                      <path d="M54.452 94.173c-.372.279-10.862 3.807-16.618 4.178V76.813c3.342.092 9.655 1.485 13.647 2.971.928.464 2.877-.093.835-2.135-4.735-3.806-12.162-6.685-22.002-6.685s-27.48 5.477-27.48 16.618 14.111 16.71 27.572 16.71c9.47 0 18.289-3.435 24.695-9.562 1.022-1.021-.278-.835-.649-.557zm-31.845 4.162c-8.013-1.65-13.694-5.801-13.694-10.66 0-4.857 5.686-9.007 13.694-10.658v21.318z"/>
                    </g>
                  </svg>
                </div>
              </button>
              <button
                onClick={() => setCutSoldSubTab('annecole')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'annecole'
                    ? 'text-teal-700 border-b-2 border-teal-600 bg-teal-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  {/* Anne Cole Logo */}
                  <span className="text-sm font-serif font-bold tracking-wide">ANNE COLE</span>
                </div>
              </button>
              <button
                onClick={() => setCutSoldSubTab('hurley')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'hurley'
                    ? 'text-slate-900 border-b-2 border-slate-900 bg-slate-100'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  {/* Hurley Logo - iconic H made from parentheses */}
                  <svg width="20" height="20" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M30 10 Q10 50 30 90" stroke="currentColor" strokeWidth="14" fill="none" strokeLinecap="round"/>
                    <path d="M70 10 Q90 50 70 90" stroke="currentColor" strokeWidth="14" fill="none" strokeLinecap="round"/>
                  </svg>
                  <span className="text-sm font-bold tracking-widest">HURLEY</span>
                </div>
              </button>
              <button
                onClick={() => setCutSoldSubTab('imgapparel')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'imgapparel'
                    ? 'text-[#1e3a5f] border-b-2 border-[#1e3a5f] bg-slate-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="text-sm font-bold tracking-wide">IMG APPAREL</span>
              </button>
              <button
                onClick={() => setCutSoldSubTab('inmocean')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  cutSoldSubTab === 'inmocean'
                    ? 'text-[#1e3a5f] border-b-2 border-[#1e3a5f] bg-slate-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <img src="/tenants/inmocean/config/logo.png" alt="InMocean" className="h-8" />
                </div>
              </button>
            </div>
          </div>

          {/* CHA-394 — Season snapshot (CHA-292) demoted BELOW the customer bar as a collapsed
              strip; expanding mounts the component (which triggers its fetch). */}
          <div className="bg-carbon-white border border-carbon-border">
            <button
              type="button"
              onClick={() => setShowSeasonSnapshot(v => !v)}
              aria-expanded={showSeasonSnapshot}
              data-testid="season-snapshot-toggle"
              className="w-full px-[18px] py-2.5 flex items-center justify-between hover:bg-carbon-gray-10 transition-colors"
            >
              <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Season snapshot</span>
              <span className="flex items-center gap-1.5 font-plex-mono text-[10px] uppercase tracking-[0.1em] text-carbon-gray-50">
                {showSeasonSnapshot ? 'Hide' : 'Show'}
                <ChevronDown size={14} className={`transition-transform ${showSeasonSnapshot ? 'rotate-180' : ''}`} />
              </span>
            </button>
            {showSeasonSnapshot && (
              <div className="border-t border-carbon-border p-4">
                <SeasonCutAndSoldSnapshot />
              </div>
            )}
          </div>

          {/* Sub-tab content */}
          {cutSoldSubTab === 'walmart' && <WalmartProductionSalesTest />}
          {cutSoldSubTab === 'target' && <TargetProductionSalesHybrid />}
          {cutSoldSubTab === 'costco' && <CostcoProductionSalesDashboard />}
          {cutSoldSubTab === 'sams' && <SamsProductionSalesDashboard />}
          {cutSoldSubTab === 'champion' && <ChampionProductionSalesDashboard />}
          {cutSoldSubTab === 'annecole' && <AnneColeCutAndSoldDashboard />}
          {cutSoldSubTab === 'hurley' && <HurleyCutAndSoldDashboard />}
          {cutSoldSubTab === 'imgapparel' && <IMGApparelCutAndSoldDashboard />}
          {cutSoldSubTab === 'inmocean' && <InMoceanProductionSalesDashboard />}
        </div>
      )}

      {/* ==================== PRE ALLOCATION TAB CONTENT ==================== */}
      {/* CHA-652 — read-only; the callable enforces the tenant's Orders grant server-side. */}
      {activeTab === 'pre-allocation' && <PreAllocationDashboard tenantId={tenantId} />}

      {/* ==================== TARGET BALANCE SHEET TAB CONTENT ==================== */}
      {/* Gated on data/target (CHA-557): even if activeTab is stale, a role
          without the grant renders nothing here. */}
      {activeTab === 'target-balance' && canSeeTarget && (
        <div className="space-y-4">
          {/* Upload / Balance Sheet sub-toggle — both surfaces moved from the
              old Data/Target section (Codex P2: keep the dashboard reachable). */}
          <div className="flex items-center gap-1 border-b border-carbon-border">
            {[
              { key: 'upload', label: 'Upload' },
              { key: 'balance-sheet', label: 'Balance Sheet' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTargetTab(t.key)}
                className={`font-plex-mono text-[11px] px-3 py-2 border-b-2 transition-colors ${
                  targetTab === t.key
                    ? 'border-carbon-ink text-carbon-ink'
                    : 'border-transparent text-carbon-gray-70 hover:text-carbon-ink'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {targetTab === 'upload' && <TargetBalanceSheetWorkflow tenantId={tenantId} />}
          {targetTab === 'balance-sheet' && <TargetBalanceSheetDashboard />}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CHA-274 — Carbon primitives (chips, summary strip, reference table, drawer)
// ============================================================================

// Mono filter chip — active = ink bg, white text (concept filter-bar anatomy).
const carbonChipCls = (active) =>
  `font-plex-mono text-[11px] px-[11px] py-[5px] border transition-colors ${
    active
      ? 'bg-carbon-ink text-carbon-white border-carbon-ink'
      : 'bg-carbon-white text-carbon-gray-70 border-carbon-border hover:border-carbon-gray-50'
  }`;

const countNote = (count) =>
  count === undefined || count === null ? undefined : `${new Intl.NumberFormat().format(count)} records`;

// Per-tab summary strip: per-tile RIGHT borders on a single bordered container
// (README is explicit: never a gray-gap tile grid here), mono KPI numerals.
const CarbonStatStrip = ({ tiles, loading }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] bg-carbon-white border border-carbon-border border-r-0">
    {tiles.map((t) => (
      <div key={t.label} className="px-4 py-3 border-r border-carbon-border">
        <div className="font-plex-mono text-[9.5px] tracking-[0.12em] uppercase text-carbon-gray-50">{t.label}</div>
        <div className="font-plex-mono text-[19px] font-semibold mt-1" style={{ color: t.color || '#161616' }}>
          {loading ? '…' : t.value}
        </div>
        {!loading && t.note ? (
          <div className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">{t.note}</div>
        ) : null}
      </div>
    ))}
  </div>
);

// GP color coding per the concept: red below 10, orange below 20, ink otherwise.
const gpTone = (gp) => (gp === null || gp === undefined ? '#8D8D8D' : gp < 10 ? '#DA1E28' : gp < 20 ? '#BA4E00' : '#161616');

// ============================================================================
// CHA-292 — Season snapshot (Cut & Sold tab, above the vendor sub-dashboards)
// ============================================================================
// Whole-season Cut & Sold rollup from the getSeasonCutAndSold callable (which
// aggregates vw_production_vs_sales with the app's Row_Type semantics —
// production columns from Production rows, sales columns across Production-
// matched + Transfer Sale rows). Presentation follows the CHA-274 idioms:
// mono season chips, per-tile-border summary strip, mono grids, TOTAL on
// gray-10, canonical Skeleton/NoResults/ErrorBanner states.

const snapQty = (v) => new Intl.NumberFormat('en-US').format(Math.round(Number(v) || 0));

// WIP(received) · IN TRANSIT · OPEN · PICK · INVOICED units + OPEN $ + INVOICED $ (reskin concept columns).
const SNAP_COLS = [
  { key: 'receivedQty', label: 'WIP', fmt: snapQty },
  { key: 'inTransitQty', label: 'In Transit', fmt: snapQty },
  { key: 'openQty', label: 'Open', fmt: snapQty },
  { key: 'onPickQty', label: 'Pick', fmt: snapQty },
  { key: 'invoicedQty', label: 'Invoiced', fmt: snapQty },
  { key: 'openRevenue', label: 'Open $', fmt: fmtMoneyCompact },
  { key: 'invoicedRevenue', label: 'Invoiced $', fmt: fmtMoneyCompact },
];

// Grouped rollup table (BY CUSTOMER / BY PROGRAM): mono uppercase header row on
// gray-10, mono numerals, TOTAL row on gray-10. Rows arrive server-sorted by
// (open + invoiced) revenue desc and are rendered in that order.
const SnapshotRollupTable = ({ eyebrow, nameLabel, rows, loading }) => {
  const totals = rows.reduce((acc, r) => {
    SNAP_COLS.forEach((c) => { acc[c.key] = (acc[c.key] || 0) + (Number(r[c.key]) || 0); });
    return acc;
  }, {});
  return (
    <div className={`bg-carbon-white border border-carbon-border overflow-hidden ${loading ? 'opacity-60' : ''}`} data-testid={`season-snapshot-${nameLabel.toLowerCase()}`}>
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
        <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">{eyebrow}</h3>
        <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">{rows.length} rows • open + invoiced $ desc</p>
      </div>
      {rows.length === 0 ? (
        <NoResults />
      ) : (
        <div className="overflow-x-auto" style={{ maxHeight: '360px' }}>
          <table className="w-full text-xs" style={{ minWidth: '760px' }}>
            <thead className="sticky top-0 bg-carbon-gray-10 z-10">
              <tr>
                <th className="px-4 py-2.5 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">{nameLabel}</th>
                {SNAP_COLS.map((c) => (
                  <th key={c.key} className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-700">
                    {r.name}
                    <span className="block font-plex-mono text-[9.5px] text-carbon-gray-50">{snapQty(r.styleCount)} STYLES</span>
                  </td>
                  {SNAP_COLS.map((c) => (
                    <td key={c.key} className="px-4 py-2 text-right font-plex-mono tabular-nums text-slate-600">{c.fmt(r[c.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
              <tr className="font-semibold text-slate-700">
                <td className="px-4 py-2.5">TOTAL</td>
                {SNAP_COLS.map((c) => (
                  <td key={c.key} className="px-4 py-2.5 text-right font-plex-mono tabular-nums">{c.fmt(totals[c.key])}</td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

function SeasonCutAndSoldSnapshot() {
  const [snap, setSnap] = useState(null);
  const [season, setSeason] = useState(null); // null = server default (newest season)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0); // stale-response guard (§6.3): only the LATEST request may land

  const fetchSnapshot = useCallback(async (target) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getSeasonCutAndSold = httpsCallable(functions, 'getSeasonCutAndSold');
      const result = await getSeasonCutAndSold(target ? { season: target } : {});
      if (reqId !== reqIdRef.current) return; // superseded by a newer season switch — drop
      setSnap(result.data);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      console.error('Error fetching season snapshot:', err);
      setError(err.message);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSnapshot(season); }, [fetchSnapshot, season]);

  const activeSeason = season ?? snap?.season;
  const totals = snap?.totals || {};

  return (
    <div className="space-y-4" data-testid="season-snapshot">
      {/* Header card: title + mono season selector chips */}
      <div className="bg-carbon-white border border-carbon-border px-[18px] py-2.5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Season snapshot</h3>
          <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">All customers &amp; programs • production vs sales</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {(snap?.seasons || []).map((s) => (
            <button
              key={s}
              onClick={() => setSeason(s)}
              aria-pressed={activeSeason === s}
              className={carbonChipCls(activeSeason === s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <ErrorBanner
          what="Couldn't load the season snapshot."
          staleNote={error}
          onRetry={() => fetchSnapshot(season)}
        />
      ) : !snap ? (
        <div className="bg-carbon-white border border-carbon-border">
          <SkeletonRows rows={4} />
        </div>
      ) : (
        <>
          {/* Summary strip — per-tile borders (never a gray-gap grid) */}
          <CarbonStatStrip
            loading={loading}
            tiles={[
              { label: 'RECEIVED', value: snapQty(totals.receivedQty), note: 'units' },
              { label: 'IN TRANSIT', value: snapQty(totals.inTransitQty), note: 'units' },
              { label: 'OPEN', value: snapQty(totals.openQty), note: 'units' },
              { label: 'ON PICK', value: snapQty(totals.onPickQty), note: 'units' },
              { label: 'INVOICED', value: snapQty(totals.invoicedQty), note: fmtMoneyCompact(totals.invoicedRevenue) },
            ]}
          />

          {/* Grouped rollups */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <SnapshotRollupTable eyebrow="By customer" nameLabel="Customer" rows={snap.byCustomer || []} loading={loading} />
            <SnapshotRollupTable eyebrow="By program" nameLabel="Program" rows={snap.byProgram || []} loading={loading} />
          </div>
        </>
      )}
    </div>
  );
}

// Bordered mono status chips. Real Open-tab statuses (sts): P = pick issued,
// O = open confirmed, B = bulk. Concept mapping: OPEN gray / PICKED-family
// blue / INVOICED green; BULK keeps its established orange identity.
const STS_CHIP = {
  P: { label: 'PICK', ink: '#0043CE', border: '#4589FF' },
  O: { label: 'OPEN', ink: '#525252', border: '#C6C6C6' },
  B: { label: 'BULK', ink: '#BA4E00', border: '#FF832B' },
};

// Bulk is classified server-side as Sts='O' AND Conf_Type='B' (the callable
// maps Conf_Type → row.confType; getSalesData never returns sts 'B'), so the
// display/filter key must derive from confType or bulk rows read as OPEN and
// the BULK chip matches nothing (Codex, #196).
const stsKeyOf = (r) => (r?.confType === 'B' ? 'B' : r?.sts);

const StatusChip = ({ sts }) => {
  const c = STS_CHIP[sts] || { label: sts || '—', ink: '#525252', border: '#C6C6C6' };
  return (
    <span
      data-testid="row-status"
      className="font-plex-mono text-[9.5px] tracking-[0.08em] border px-2 py-[3px] w-fit whitespace-nowrap"
      style={{ color: c.ink, borderColor: c.border }}
    >
      {c.label}
    </span>
  );
};

const poKeyOf = (v) => String(v ?? '').trim();
const PAGE_SIZE = 10;

// The reference table pattern (Orders concept §6): eyebrow header, filter bar
// attached to the table top (search cell | mono status chips), mono uppercase
// header row on gray-10, CSS-grid data rows with a 3px severity rail + bot
// mark for POs with open agent tickets, totals row, mono pagination footer,
// and a 460px detail drawer. Presentation only — callers pass today's rows,
// reducers and export handlers unchanged.
const CarbonPoTable = ({
  eyebrow,
  meta,
  columns,
  rows,
  loading,
  emptyText,
  minWidth = '1080px',
  searchPlaceholder,
  searchFn,
  statusFilters,
  statusOf,
  poOf,
  ticketsByPo,
  onExport,
  totalsCells,
  buildDrawer,
  testId,
}) => {
  const [query, setQuery] = useState('');
  const [statusKey, setStatusKey] = useState('all');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  const q = query.trim().toLowerCase();
  const filtered = rows.filter(
    (r) => (!q || searchFn(r, q)) && (!statusFilters || statusKey === 'all' || statusOf(r) === statusKey)
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageClamped = Math.min(page, pageCount - 1);
  const slice = filtered.slice(pageClamped * PAGE_SIZE, pageClamped * PAGE_SIZE + PAGE_SIZE);

  const gridTemplate = [...columns.map((c) => c.width), '32px'].join(' ');
  const ticketFor = (r) => (ticketsByPo && ticketsByPo.get(poKeyOf(poOf(r)))) || null;
  const showSkeleton = loading && rows.length === 0;
  const totals = !showSkeleton && filtered.length > 0 && totalsCells ? totalsCells(filtered) : null;
  const clearFilters = () => {
    setQuery('');
    setStatusKey('all');
    setPage(0);
  };

  return (
    <div
      className={`bg-carbon-white border border-carbon-border ${loading && rows.length > 0 ? 'opacity-60' : ''}`}
      data-testid={testId}
    >
      {/* Eyebrow header */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
        <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">
          {eyebrow}
        </span>
        <div className="flex items-center gap-3">
          {meta ? <span className="font-plex-mono text-[10px] uppercase text-carbon-gray-50">{meta}</span> : null}
          {onExport ? (
            <button
              onClick={onExport}
              className="flex items-center gap-1.5 bg-carbon-white border border-carbon-gray-30 text-carbon-ink px-2.5 py-1 text-[11px] font-medium hover:bg-carbon-gray-10"
              title="Export to Excel"
            >
              <Download size={12} /> Export
            </button>
          ) : null}
        </div>
      </div>

      {/* Filter bar attached to table top */}
      <div className="flex items-center flex-wrap border-b border-carbon-border">
        <div className="flex items-center gap-2 px-3.5 py-2 border-r border-carbon-border flex-1 min-w-[220px]">
          <Search size={15} className="text-carbon-gray-50 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
            placeholder={searchPlaceholder}
            className="border-none outline-none text-[13px] flex-1 bg-transparent text-carbon-ink placeholder:text-carbon-gray-40 min-w-0"
          />
          {query ? (
            <button
              onClick={() => {
                // Clear ONLY the search — an active status chip survives.
                setQuery('');
                setPage(0);
              }}
              aria-label="Clear search"
              className="bg-transparent border-none cursor-pointer text-carbon-gray-50 hover:text-carbon-ink flex p-0.5"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
        {statusFilters ? (
          <div className="flex items-center px-3.5 py-1.5 gap-1.5 flex-wrap">
            <span className="font-plex-mono text-[9.5px] tracking-[0.12em] text-carbon-gray-50 mr-1">STATUS</span>
            {[{ key: 'all', label: 'ALL' }, ...statusFilters].map((f) => (
              <button
                key={f.key}
                onClick={() => {
                  setStatusKey(f.key);
                  setPage(0);
                }}
                className={carbonChipCls(statusKey === f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <div style={{ minWidth }}>
          <div
            className="grid gap-3 px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10 items-center"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            {columns.map((c) => (
              <span
                key={c.key}
                className={`font-plex-mono text-[10px] tracking-[0.12em] text-carbon-gray-50 ${c.align === 'right' ? 'text-right' : ''}`}
              >
                {c.label}
              </span>
            ))}
            <span />
          </div>

          {showSkeleton ? (
            <SkeletonRows rows={6} />
          ) : filtered.length === 0 ? (
            rows.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-carbon-gray-50">{emptyText}</div>
            ) : (
              <NoResults query={query || undefined} onClear={clearFilters} />
            )
          ) : (
            slice.map((r, i) => {
              const t = ticketFor(r);
              const rv = t ? rowViewOf(t) : null;
              const isSel = selected === r;
              return (
                <button
                  key={pageClamped * PAGE_SIZE + i}
                  type="button"
                  data-testid="orders-row"
                  onClick={() => setSelected(r)}
                  className={`relative w-full text-left grid gap-3 px-[18px] py-[11px] border-t border-carbon-gray-10 items-center hover:bg-[#F8F8F8] ${
                    isSel ? 'bg-carbon-blue-10' : 'bg-carbon-white'
                  }`}
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  {rv ? (
                    <span
                      data-testid="agent-rail"
                      className="absolute left-0 top-0 bottom-0 w-[3px]"
                      style={{ background: rv.sev }}
                    />
                  ) : null}
                  {columns.map((c) => (
                    <React.Fragment key={c.key}>{c.render(r)}</React.Fragment>
                  ))}
                  {t ? (
                    <Bot size={14} data-testid="agent-bot" title="Agent ticket open" style={{ color: rv.agent.color }} />
                  ) : (
                    <span />
                  )}
                </button>
              );
            })
          )}

          {totals ? (
            <div
              className="grid gap-3 px-[18px] py-2.5 border-t border-carbon-border bg-carbon-gray-10 items-center font-plex-mono text-[11.5px] font-semibold text-carbon-ink"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {columns.map((c, i) => (
                <span key={c.key} className={c.align === 'right' ? 'text-right' : ''}>
                  {i === 0 ? 'TOTAL' : totals[c.key] ?? ''}
                </span>
              ))}
              <span />
            </div>
          ) : null}

          {/* Pagination footer */}
          {!showSkeleton ? (
            <div className="flex items-center justify-between px-[18px] py-2 border-t border-carbon-border bg-carbon-gray-10">
              <span className="font-plex-mono text-[11px] text-carbon-gray-50">
                {filtered.length === 0
                  ? '0 RESULTS'
                  : `${pageClamped * PAGE_SIZE + 1}–${Math.min(filtered.length, (pageClamped + 1) * PAGE_SIZE)} OF ${filtered.length}`}
              </span>
              <div className="flex gap-px">
                <button
                  onClick={() => setPage(Math.max(0, pageClamped - 1))}
                  aria-label="Previous page"
                  className={`w-8 h-8 flex items-center justify-center bg-carbon-white border border-carbon-border ${
                    pageClamped > 0 ? 'text-carbon-ink hover:bg-carbon-gray-10' : 'text-carbon-gray-30 cursor-default'
                  }`}
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  onClick={() => setPage(Math.min(pageCount - 1, pageClamped + 1))}
                  aria-label="Next page"
                  className={`w-8 h-8 flex items-center justify-center bg-carbon-white border border-carbon-border ${
                    pageClamped < pageCount - 1 ? 'text-carbon-ink hover:bg-carbon-gray-10' : 'text-carbon-gray-30 cursor-default'
                  }`}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {selected ? <OrderDrawer sel={buildDrawer(selected)} ticket={ticketFor(selected)} onClose={() => setSelected(null)} /> : null}
    </div>
  );
};

// 460px detail drawer: mono PO title, 1px-gap stat grid, agent-ticket callout
// (tinted per agent, cockpit cross-link) and a TIMELINE built only from
// fields the row actually carries — no invented events.
const OrderDrawer = ({ sel, ticket, onClose }) => {
  const rv = ticket ? rowViewOf(ticket) : null;
  const timeline = [...sel.timeline, ...(ticket ? [{ date: 'now', text: 'Agent ticket open — see cockpit' }] : [])];
  // Keyboard support: Escape closes; focus lands on the close button on open
  // and returns to the previously-focused element (the row) on unmount.
  const closeBtnRef = React.useRef(null);
  React.useEffect(() => {
    const prev = document.activeElement;
    closeBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end font-plex text-carbon-ink">
      <style>{`@keyframes carbonSlideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
      <div className="absolute inset-0 bg-[rgba(22,22,22,0.5)]" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Purchase order ${sel.title}`}
        className="relative w-full max-w-[460px] bg-carbon-gray-10 h-full flex flex-col"
        style={{ boxShadow: '-24px 0 60px rgba(22,22,22,0.25)', animation: 'carbonSlideIn 0.25s ease both' }}
      >
        <div className="px-6 py-[18px] border-b border-carbon-border bg-carbon-white flex items-start justify-between">
          <div>
            <div className="font-plex-mono text-[10px] tracking-[0.14em] text-carbon-gray-50">PURCHASE ORDER</div>
            <h2 className="font-plex-mono text-[21px] font-semibold mt-1 tracking-[-0.01em]">{sel.title}</h2>
            {sel.subtitle ? <div className="text-[13px] text-carbon-gray-70 mt-0.5">{sel.subtitle}</div> : null}
          </div>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close"
            className="text-carbon-gray-50 hover:text-carbon-ink bg-transparent border-none cursor-pointer flex p-1"
          >
            <X size={19} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-px bg-carbon-border border border-carbon-border">
            {sel.stats.map((st) => (
              <div key={st.label} className="bg-carbon-white px-3.5 py-2.5">
                <div className="font-plex-mono text-[9.5px] tracking-[0.1em] text-carbon-gray-50">{st.label}</div>
                <div className="font-plex-mono text-[15px] font-semibold mt-1" style={{ color: st.color || '#161616' }}>
                  {st.value}
                </div>
              </div>
            ))}
          </div>
          {ticket && rv ? (
            <div className="px-4 py-3.5 border" style={{ background: rv.agent.tint, borderColor: rv.agent.color }}>
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles size={13} style={{ color: rv.agent.color }} />
                <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase" style={{ color: rv.agent.color }}>
                  {rv.agent.name.toUpperCase()}&rsquo;S TICKET — {String(rv.agent.sub || '').toUpperCase()}
                </span>
              </div>
              <div className="text-[13px] leading-relaxed text-carbon-gray-80">
                {rv.metric} {rv.caption} — {rv.suggested} · {fmtMoney(exposureOf(ticket))} exposure
              </div>
              <a
                href="#/agents"
                className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-carbon-blue-60 hover:text-carbon-blue-70 mt-2"
              >
                Open in agents cockpit <ArrowUpRight size={13} />
              </a>
            </div>
          ) : null}
          <div className="bg-carbon-white border border-carbon-border px-4 py-3.5">
            <div className="font-plex-mono text-[10px] tracking-[0.12em] text-carbon-gray-50 mb-2">TIMELINE</div>
            {timeline.length === 0 ? (
              <div className="text-xs text-carbon-gray-50 py-1">No dated events on this row.</div>
            ) : (
              timeline.map((tl, i) => (
                <div key={i} className="flex gap-2.5 py-1.5 border-t border-carbon-gray-10 first:border-t-0">
                  <span className="font-plex-mono text-[10.5px] text-carbon-gray-40 w-[76px] flex-shrink-0 pt-px">
                    {tl.date}
                  </span>
                  <span className="text-[12.5px]" style={{ color: tl.color || '#525252' }}>
                    {tl.text}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// INVOICE BY MONTH TABLE
// ============================================================================
const InvoiceByMonthTable = ({ data, formatCurrency, loading, selectedMonth, onRowClick }) => {
  if (!data) {
    return (
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Invoice by Month</h3>
        </div>
        <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  const totalAmount = data.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalGrossProfit = data.reduce((sum, row) => sum + (row.grossProfit || 0), 0);
  const avgGP = totalAmount > 0 ? (totalGrossProfit / totalAmount) * 100 : 0;

  return (
    <div className={`bg-carbon-white border ${selectedMonth ? 'border-carbon-blue-60' : 'border-carbon-border'} overflow-hidden ${loading ? 'opacity-60' : ''}`}>
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-green-100 flex items-center justify-center">
            <FileText size={12} className="text-green-600" />
          </div>
          <div>
            <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Invoice by Month</h3>
            <p className="text-[10px] text-slate-400">{data.length} months • Click to filter</p>
          </div>
        </div>
        {selectedMonth && (
          <button onClick={() => onRowClick(selectedMonth)} className="font-plex-mono text-[10.5px] px-2 py-1 border border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10">Clear</button>
        )}
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: '300px' }}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-carbon-gray-10 z-10">
            <tr>
              <th className="px-4 py-2.5 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Month</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Amount</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Gross Profit</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">GP%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => {
              const isSelected = selectedMonth === row.month;
              return (
                <tr key={idx} onClick={() => onRowClick && onRowClick(row.month)} className={`cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-green-100 hover:bg-green-200' : 'hover:bg-slate-50'}`}>
                  <td className={`px-4 py-2 font-medium ${isSelected ? 'text-green-800' : 'text-slate-700'}`}>{row.monthLabel}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{formatCurrency(row.amount)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{formatCurrency(row.grossProfit)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${row.gpPercent < 0 ? 'text-red-600' : row.gpPercent > 30 ? 'text-green-600' : 'text-slate-600'}`}>{row.gpPercent?.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
          {data.length > 0 && (
            <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
              <tr className="font-semibold text-slate-700">
                <td className="px-4 py-2.5">TOTAL</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(totalAmount)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(totalGrossProfit)}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums ${avgGP < 0 ? 'text-red-600' : avgGP > 30 ? 'text-green-600' : ''}`}>{avgGP.toFixed(1)}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// INVOICE CUSTOMER SUMMARY TABLE
// ============================================================================
const InvoiceCustomerSummaryTable = ({ data, formatCurrency, loading, selectedMonth, selectedMonthLabel, selectedCustomer, onRowClick }) => {
  if (!data) {
    return (
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Customer Summary</h3>
        </div>
        <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  const totalAmount = data.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalGrossProfit = data.reduce((sum, row) => sum + (row.grossProfit || 0), 0);
  const avgGP = totalAmount > 0 ? (totalGrossProfit / totalAmount) * 100 : 0;

  return (
    <div className={`bg-carbon-white border ${selectedCustomer ? 'border-carbon-blue-60' : 'border-carbon-border'} overflow-hidden ${loading ? 'opacity-60' : ''}`}>
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-100 flex items-center justify-center">
            <Users size={12} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Customer Summary</h3>
            <p className="text-[10px] text-slate-400">
              {data.length} customers
              {selectedMonth && <span className="text-blue-600 font-medium"> • {selectedMonthLabel}</span>}
            </p>
          </div>
        </div>
        {selectedCustomer && (
          <button onClick={() => onRowClick(selectedCustomer)} className="font-plex-mono text-[10.5px] px-2 py-1 border border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10">Clear</button>
        )}
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: '300px' }}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-carbon-gray-10 z-10">
            <tr>
              <th className="px-4 py-2.5 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Customer Name</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Amount</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Gross Profit</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">GP%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => {
              const isSelected = selectedCustomer === row.customerName;
              return (
                <tr key={idx} onClick={() => onRowClick && onRowClick(row.customerName)} className={`cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-slate-50'}`}>
                  <td className={`px-4 py-2 font-medium max-w-48 truncate ${isSelected ? 'text-blue-800' : 'text-slate-700'}`} title={row.customerName}>{row.customerName}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{formatCurrency(row.amount)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{formatCurrency(row.grossProfit)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${row.gpPercent < 0 ? 'text-red-600' : row.gpPercent > 30 ? 'text-green-600' : 'text-slate-600'}`}>{row.gpPercent?.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
          {data.length > 0 && (
            <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
              <tr className="font-semibold text-slate-700">
                <td className="px-4 py-2.5">TOTAL</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(totalAmount)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(totalGrossProfit)}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums ${avgGP < 0 ? 'text-red-600' : avgGP > 30 ? 'text-green-600' : ''}`}>{avgGP.toFixed(1)}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// MTD BY DAY TABLE
// ============================================================================
const MTDByDayTable = ({ data, formatCurrency, loading, selectedDay, selectedMonth, selectedMonthLabel, onRowClick }) => {
  if (!data) {
    return (
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">By Day</h3>
        </div>
        <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  const totalAmount = data.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalGrossProfit = data.reduce((sum, row) => sum + (row.grossProfit || 0), 0);
  const avgGP = totalAmount > 0 ? (totalGrossProfit / totalAmount) * 100 : 0;

  const displayMonth = selectedMonth && selectedMonthLabel
    ? selectedMonthLabel
    : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const tableTitle = selectedMonth ? 'By Day' : 'MTD by Day';

  return (
    <div className={`bg-carbon-white border ${selectedDay ? 'border-carbon-blue-60' : 'border-carbon-border'} overflow-hidden ${loading ? 'opacity-60' : ''}`}>
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-purple-100 flex items-center justify-center">
            <CalendarDays size={12} className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">{tableTitle}</h3>
            <p className="text-[10px] text-slate-400">{displayMonth} • {data.length} days</p>
          </div>
        </div>
        {selectedDay && (
          <button onClick={() => onRowClick(selectedDay)} className="font-plex-mono text-[10.5px] px-2 py-1 border border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10">Clear</button>
        )}
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: '300px' }}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-carbon-gray-10 z-10">
            <tr>
              <th className="px-4 py-2.5 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Day</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Amount</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">GP</th>
              <th className="px-4 py-2.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">GP%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => {
              const isSelected = selectedDay === row.date;
              return (
                <tr key={idx} onClick={() => onRowClick && onRowClick(row.date)} className={`cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-purple-100 hover:bg-purple-200' : 'hover:bg-slate-50'}`}>
                  <td className={`px-4 py-2 font-medium ${isSelected ? 'text-purple-800' : 'text-slate-700'}`}>{row.dayLabel}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{formatCurrency(row.amount)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{formatCurrency(row.grossProfit)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${row.gpPercent < 0 ? 'text-red-600' : row.gpPercent > 30 ? 'text-green-600' : 'text-slate-600'}`}>{row.gpPercent?.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
          {data.length > 0 && (
            <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
              <tr className="font-semibold text-slate-700">
                <td className="px-4 py-2.5">TOTAL</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(totalAmount)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(totalGrossProfit)}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums ${avgGP < 0 ? 'text-red-600' : avgGP > 30 ? 'text-green-600' : ''}`}>{avgGP.toFixed(1)}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// INVOICE DETAILS TABLE — reference table pattern (CHA-274)
// ============================================================================
const InvoiceDetailsTable = ({ data, formatCurrency, formatNumber, loading, selectedMonth, selectedCustomer, selectedDay, selectedMonthLabel, ticketsByPo }) => {
  const rows = data || [];

  const filterParts = [];
  if (selectedMonth) filterParts.push(selectedMonthLabel);
  if (selectedCustomer) filterParts.push(selectedCustomer);
  if (selectedDay) filterParts.push(selectedDay.split('T')[0]);
  const filterDesc = filterParts.length > 0 ? filterParts.join(' • ') : 'All invoices';

  // Export is byte-identical: always the full dataset the callable returned,
  // regardless of the client-side search filter.
  const handleExport = () => {
    const exportData = rows.map(row => ({
      'Division': row.divisionName,
      'Warehouse': row.warehouse,
      'Customer': row.customerName,
      'Order Qty': row.orderQty,
      'Amount': row.amount,
      'GP%': row.gpPercent?.toFixed(1),
      'PO#': row.poNo,
      'Pick#': row.pickNumber,
      'Style Season': row.styleSeason,
      'Invoice Date': row.invoiceDate?.split('T')[0] || '',
      'Style': row.style,
      'Style Desc': row.styleDesc,
      'Sales Person': row.salesPersonCode
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice Details');
    XLSX.writeFile(wb, `InvoiceDetails_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const dateOf = (v) => (v ? String(v).split('T')[0] : '—');
  const mono = 'font-plex-mono text-[11.5px] text-carbon-gray-70 whitespace-nowrap';
  const columns = [
    { key: 'poNo', label: 'PO', width: '84px', render: (r) => <span className="font-plex-mono text-[12px] font-semibold text-carbon-ink whitespace-nowrap">{r.poNo}</span> },
    { key: 'customerName', label: 'CUSTOMER', width: 'minmax(130px,1.3fr)', render: (r) => <span className="text-[12.5px] text-carbon-ink overflow-hidden text-ellipsis whitespace-nowrap" title={r.customerName}>{r.customerName}</span> },
    { key: 'divisionName', label: 'DIVISION', width: 'minmax(90px,1fr)', render: (r) => <span className="text-[12px] text-carbon-gray-70 overflow-hidden text-ellipsis whitespace-nowrap" title={r.divisionName}>{r.divisionName}</span> },
    { key: 'warehouse', label: 'WH', width: '44px', render: (r) => <span className={mono}>{r.warehouse}</span> },
    { key: 'style', label: 'STYLE', width: '84px', render: (r) => <span className={mono}>{r.style}</span> },
    { key: 'styleDesc', label: 'STYLE DESC', width: 'minmax(110px,1fr)', render: (r) => <span className="text-[12px] text-carbon-gray-70 overflow-hidden text-ellipsis whitespace-nowrap" title={r.styleDesc}>{r.styleDesc}</span> },
    { key: 'styleSeason', label: 'SEASON', width: '64px', render: (r) => <span className={mono}>{r.styleSeason}</span> },
    { key: 'invoiceDate', label: 'INVOICED', width: '88px', render: (r) => <span className={mono}>{dateOf(r.invoiceDate)}</span> },
    { key: 'pickNumber', label: 'PICK #', width: '72px', render: (r) => <span className={mono}>{r.pickNumber}</span> },
    { key: 'salesPersonCode', label: 'SALES', width: '52px', render: (r) => <span className={mono}>{r.salesPersonCode}</span> },
    { key: 'orderQty', label: 'QTY', width: '64px', align: 'right', render: (r) => <span className={`${mono} text-right block`}>{formatNumber(r.orderQty)}</span> },
    { key: 'amount', label: 'AMOUNT', width: '88px', align: 'right', render: (r) => <span className="font-plex-mono text-[12px] font-semibold text-carbon-ink text-right block whitespace-nowrap">{formatCurrency(r.amount)}</span> },
    { key: 'gpPercent', label: 'GP %', width: '58px', align: 'right', render: (r) => <span className="font-plex-mono text-[12px] text-right block" style={{ color: gpTone(r.gpPercent) }}>{r.gpPercent != null ? `${r.gpPercent.toFixed(1)}%` : '—'}</span> },
  ];

  // Same reducers as before, over the rows currently matched by the filters.
  const totalsCells = (visible) => {
    const totalAmount = visible.reduce((sum, row) => sum + (row.amount || 0), 0);
    const totalQty = visible.reduce((sum, row) => sum + (row.orderQty || 0), 0);
    const weightedGP = totalAmount > 0
      ? visible.reduce((sum, row) => sum + ((row.amount || 0) * (row.gpPercent || 0)), 0) / totalAmount
      : 0;
    return {
      orderQty: formatNumber(totalQty),
      amount: formatCurrency(totalAmount),
      gpPercent: `${weightedGP.toFixed(1)}%`,
    };
  };

  const buildDrawer = (r) => ({
    title: r.poNo || '—',
    subtitle: [r.customerName, r.divisionName].filter(Boolean).join(' · '),
    stats: [
      { label: 'AMOUNT', value: formatCurrency(r.amount) },
      { label: 'GP %', value: r.gpPercent != null ? `${r.gpPercent.toFixed(1)}%` : '—', color: gpTone(r.gpPercent) },
      { label: 'QTY', value: formatNumber(r.orderQty) },
      { label: 'INVOICE DATE', value: dateOf(r.invoiceDate) },
      { label: 'SEASON', value: r.styleSeason || '—' },
      { label: 'PICK #', value: r.pickNumber || '—' },
    ],
    timeline: [
      r.invoiceDate ? { date: dateOf(r.invoiceDate), text: 'Invoiced' } : null,
    ].filter(Boolean),
  });

  return (
    <CarbonPoTable
      eyebrow="Invoice Details"
      meta={`${rows.length} records • ${filterDesc}`}
      columns={columns}
      rows={rows}
      loading={loading || !data}
      emptyText="No invoice details found"
      minWidth="1180px"
      searchPlaceholder="Search PO, style, customer…"
      searchFn={(r, q) =>
        [r.poNo, r.customerName, r.style, r.styleDesc, r.divisionName, r.pickNumber]
          .some((v) => String(v ?? '').toLowerCase().includes(q))
      }
      poOf={(r) => r.poNo}
      ticketsByPo={ticketsByPo}
      onExport={handleExport}
      totalsCells={totalsCells}
      buildDrawer={buildDrawer}
      testId="invoice-details-table"
    />
  );
};

// ============================================================================
// TRANSPOSED MATRIX COMPONENT (for Open/Pick tab)
// ============================================================================
const TransposedMatrix = ({ data, selectedCells = [], onCellClick, formatCurrency, formatNumber, viewMode = 'all' }) => {
  if (!data?.matrix || !data?.shipWindowColumns) {
    return <div className="p-4 text-slate-400">No data</div>;
  }

  const toNum = (val) => Number(val) || 0;

  const isCellSelected = (division, shipWindow) => {
    return selectedCells.some(c => c.division === division && c.shipWindow === shipWindow);
  };

  const getFieldKey = (shipWindow) => {
    if (viewMode === 'pick') return `${shipWindow}_pick`;
    if (viewMode === 'open') return `${shipWindow}_open`;
    if (viewMode === 'bulk') return `${shipWindow}_bulk`;
    return shipWindow;
  };

  const getCountKey = (shipWindow) => {
    if (viewMode === 'pick') return `${shipWindow}_pick_count`;
    if (viewMode === 'open') return `${shipWindow}_open_count`;
    if (viewMode === 'bulk') return `${shipWindow}_bulk_count`;
    return `${shipWindow}_count`;
  };

  const divisions = (data.divisions || []).filter(d => d && d !== 'TOTAL').sort();

  const transposedRows = data.shipWindowColumns.map(shipWindow => {
    const row = { shipWindow, total: 0 };
    divisions.forEach(div => {
      const originalRow = data.matrix.find(r => r.division === div);
      const value = toNum(originalRow?.[getFieldKey(shipWindow)]);
      const count = toNum(originalRow?.[getCountKey(shipWindow)]);
      row[div] = value;
      row[`${div}_count`] = count;
      row.total += value;
    });
    return row;
  });

  const totalRow = { shipWindow: 'TOTAL', total: 0 };
  divisions.forEach(div => {
    const colTotal = transposedRows.reduce((sum, r) => sum + toNum(r[div]), 0);
    totalRow[div] = colTotal;
    totalRow[`${div}_count`] = transposedRows.reduce((sum, r) => sum + toNum(r[`${div}_count`]), 0);
    totalRow.total += colTotal;
  });
  transposedRows.push(totalRow);

  const statusColors = {
    'Invalid Dates': { bg: 'bg-slate-50', border: 'border-l-slate-400', text: 'text-slate-500' },
    'Pre Start': { bg: 'bg-blue-50/30', border: 'border-l-blue-500', text: 'text-blue-700' },
    'In Window': { bg: 'bg-green-50/30', border: 'border-l-green-500', text: 'text-green-700' },
    'Pass Cancel': { bg: 'bg-red-50/30', border: 'border-l-red-500', text: 'text-red-700' },
    'TOTAL': { bg: 'bg-slate-100', border: 'border-l-slate-600', text: 'text-slate-800' },
  };

  const getStatusStyle = (shipWindow) => {
    const name = shipWindow.replace(/^\d\./, '');
    return statusColors[name] || { bg: '', border: 'border-l-transparent', text: 'text-slate-600' };
  };

  const splitDivName = (div) => {
    const clean = div.replace(/^\d\./, '');
    const match = clean.match(/^(.+?)(\([^)]+\))$/);
    if (match) {
      return { name: match[1].trim(), code: match[2] };
    }
    return { name: clean, code: '' };
  };

  const hasSelection = selectedCells.length > 0;

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 bg-slate-100 border-b border-slate-300 whitespace-nowrap">Status</th>
          {divisions.map((div) => {
            const { name, code } = splitDivName(div);
            const hasSelectedInCol = selectedCells.some(c => c.division === div);
            const isGrayed = hasSelection && !hasSelectedInCol;
            return (
              <th key={div} className={`px-2 py-2 text-center text-xs font-semibold border-b border-slate-300 cursor-pointer bg-slate-100 hover:bg-slate-200 ${isGrayed ? 'text-slate-300' : 'text-slate-600'}`} onClick={() => onCellClick(div, 'total')}>
                <div>{name}</div>
                {code && <div className={`text-[10px] font-normal ${isGrayed ? 'text-slate-300' : 'text-slate-400'}`}>{code}</div>}
              </th>
            );
          })}
          <th className="px-3 py-2 text-center text-xs font-bold text-slate-700 bg-slate-200 border-b border-slate-300">Total</th>
        </tr>
      </thead>
      <tbody>
        {transposedRows.map((row, idx) => {
          const isTotal = row.shipWindow === 'TOTAL';
          const hasSelectedInRow = selectedCells.some(c => c.shipWindow === row.shipWindow);
          const displayName = row.shipWindow.replace(/^\d\./, '');
          const style = getStatusStyle(row.shipWindow);
          const rowIsGrayed = hasSelection && !hasSelectedInRow && !isTotal;

          return (
            <tr key={idx} className={isTotal ? 'bg-slate-100' : 'hover:bg-slate-50'}>
              <td className={`px-3 py-2 text-xs font-medium cursor-pointer border-b border-slate-100 border-l-4 whitespace-nowrap ${style.border} ${isTotal ? 'bg-slate-100 border-b-slate-300' : 'bg-white'} ${rowIsGrayed ? 'text-slate-300' : style.text}`} onClick={() => !isTotal && onCellClick('TOTAL', row.shipWindow)}>{displayName}</td>
              {divisions.map((div) => {
                const value = toNum(row[div]);
                const cellIsSelected = isCellSelected(div, row.shipWindow);
                const isGrayed = hasSelection && !cellIsSelected;

                return (
                  <td key={div} className={`px-2 py-2 text-right text-xs tabular-nums cursor-pointer border-b border-slate-100 ${cellIsSelected ? 'bg-blue-100 text-blue-800 font-medium' : isGrayed ? 'text-slate-300' : value > 0 ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-300'} ${isTotal ? 'border-b-slate-300' : ''}`} onClick={() => value > 0 && onCellClick(div, row.shipWindow)} title={value > 0 ? `${formatNumber(row[`${div}_count`])} records` : ''}>
                    {value > 0 ? formatCurrency(value) : '—'}
                  </td>
                );
              })}
              <td className={`px-3 py-2 text-right text-xs tabular-nums font-semibold border-b ${isTotal ? 'bg-slate-200 text-slate-800 border-b-slate-300' : 'bg-slate-50 text-slate-600 border-b-slate-100'}`}>{formatCurrency(row.total)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ============================================================================
// OPEN BY STATUS TABLE
// ============================================================================
const OpenByStatusTable = ({ data, formatCurrency, loading, selectedCells = [], selectedView = 'all', onRowClick, onColumnClick }) => {
  if (!data?.matrix || !data?.shipWindowColumns) {
    return (
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">By Status</h3>
        </div>
        <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  const toNum = (val) => Number(val) || 0;
  const hasFilters = selectedCells.length > 0;

  const rows = data.shipWindowColumns.map(shipWindow => {
    let pickTotal = 0, openTotal = 0, bulkTotal = 0;

    data.matrix.forEach(divRow => {
      if (divRow.division === 'TOTAL') return;
      const shouldInclude = !hasFilters || selectedCells.some(c => c.division === divRow.division && c.shipWindow === shipWindow);
      if (shouldInclude) {
        pickTotal += toNum(divRow[`${shipWindow}_pick`]);
        openTotal += toNum(divRow[`${shipWindow}_open`]);
        bulkTotal += toNum(divRow[`${shipWindow}_bulk`]);
      }
    });

    return { shipWindow, pick: pickTotal, open: openTotal, bulk: bulkTotal, total: pickTotal + openTotal + bulkTotal };
  });

  const totalRow = rows.reduce((acc, row) => ({
    shipWindow: 'TOTAL',
    pick: acc.pick + row.pick,
    open: acc.open + row.open,
    bulk: acc.bulk + row.bulk,
    total: acc.total + row.total
  }), { shipWindow: 'TOTAL', pick: 0, open: 0, bulk: 0, total: 0 });

  const getRowStyle = (shipWindow) => {
    const name = shipWindow.replace(/^\d\./, '');
    const styles = {
      'Invalid Dates': 'border-l-slate-400 text-slate-500',
      'Pre Start': 'border-l-blue-500 text-blue-700',
      'In Window': 'border-l-green-500 text-green-700',
      'Pass Cancel': 'border-l-red-500 text-red-700',
      'TOTAL': 'border-l-slate-600 text-slate-800',
    };
    return styles[name] || 'border-l-transparent text-slate-600';
  };

  return (
    <div className={`bg-carbon-white border border-carbon-border overflow-hidden ${loading ? 'opacity-60' : ''}`}>
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center gap-2">
        <div className="w-6 h-6 bg-purple-100 flex items-center justify-center">
          <Package size={12} className="text-purple-600" />
        </div>
        <div>
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">By Status</h3>
          <p className="text-[10px] text-slate-400">Pick • Confirmed • Bulk</p>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-carbon-gray-10">
            <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border whitespace-nowrap">Status</th>
            <th className={`px-2 py-2 text-right font-semibold border-b border-slate-200 cursor-pointer transition-colors ${selectedView === 'pick' ? 'bg-blue-500 text-white' : 'text-blue-600 hover:bg-blue-50'}`} onClick={() => onColumnClick && onColumnClick('pick')}>Pick</th>
            <th className={`px-2 py-2 text-right font-semibold border-b border-slate-200 cursor-pointer transition-colors ${selectedView === 'open' ? 'bg-purple-500 text-white' : 'text-purple-600 hover:bg-purple-50'}`} onClick={() => onColumnClick && onColumnClick('open')}>Confirmed</th>
            <th className={`px-2 py-2 text-right font-semibold border-b border-slate-200 cursor-pointer transition-colors ${selectedView === 'bulk' ? 'bg-orange-500 text-white' : 'text-orange-600 hover:bg-orange-50'}`} onClick={() => onColumnClick && onColumnClick('bulk')}>Bulk</th>
            <th className="px-3 py-2 text-right font-semibold text-slate-700 border-b border-slate-200 bg-slate-100">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const displayName = row.shipWindow.replace(/^\d\./, '');
            const rowStyle = getRowStyle(row.shipWindow);
            const isRowSelected = hasFilters && selectedCells.some(c => c.shipWindow === row.shipWindow);
            const isRowGrayed = hasFilters && !isRowSelected;

            return (
              <tr key={idx} className={`cursor-pointer transition-colors ${isRowSelected ? 'bg-blue-50 hover:bg-blue-100' : isRowGrayed ? 'opacity-40 hover:opacity-60' : 'hover:bg-slate-50'}`} onClick={() => onRowClick && onRowClick(row.shipWindow)}>
                <td className={`px-3 py-1.5 font-medium border-l-4 border-b border-slate-100 whitespace-nowrap ${rowStyle}`}>{displayName}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums border-b border-slate-100 ${selectedView === 'pick' ? 'bg-blue-100 text-blue-800 font-medium' : selectedView !== 'all' ? 'text-slate-300' : 'text-slate-600'}`}>{row.pick > 0 ? formatCurrency(row.pick) : '—'}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums border-b border-slate-100 ${selectedView === 'open' ? 'bg-purple-100 text-purple-800 font-medium' : selectedView !== 'all' ? 'text-slate-300' : 'text-slate-600'}`}>{row.open > 0 ? formatCurrency(row.open) : '—'}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums border-b border-slate-100 ${selectedView === 'bulk' ? 'bg-orange-100 text-orange-800 font-medium' : selectedView !== 'all' ? 'text-slate-300' : 'text-slate-600'}`}>{row.bulk > 0 ? formatCurrency(row.bulk) : '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium border-b border-slate-100 bg-slate-50 text-slate-700">{row.total > 0 ? formatCurrency(row.total) : '—'}</td>
              </tr>
            );
          })}
          <tr className="bg-slate-100 font-semibold">
            <td className="px-3 py-2 border-l-4 border-l-slate-600 border-t border-slate-300 text-slate-800">TOTAL</td>
            <td className={`px-2 py-2 text-right tabular-nums border-t border-slate-300 ${selectedView === 'pick' ? 'bg-blue-200 text-blue-900' : selectedView !== 'all' ? 'text-slate-400' : 'text-blue-700'}`}>{formatCurrency(totalRow.pick)}</td>
            <td className={`px-2 py-2 text-right tabular-nums border-t border-slate-300 ${selectedView === 'open' ? 'bg-purple-200 text-purple-900' : selectedView !== 'all' ? 'text-slate-400' : 'text-purple-700'}`}>{formatCurrency(totalRow.open)}</td>
            <td className={`px-2 py-2 text-right tabular-nums border-t border-slate-300 ${selectedView === 'bulk' ? 'bg-orange-200 text-orange-900' : selectedView !== 'all' ? 'text-slate-400' : 'text-orange-700'}`}>{formatCurrency(totalRow.bulk)}</td>
            <td className="px-3 py-2 text-right tabular-nums border-t border-slate-300 bg-slate-200 text-slate-800">{formatCurrency(totalRow.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// ROUTING STATUS TABLE
// ============================================================================
const RoutingStatusTable = ({ data, formatCurrency, loading, selectedCells = [], onRowClick }) => {
  if (!data?.routingStatusMatrix || !data?.orderStatusColumns) {
    return (
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Order By Routing Status</h3>
        </div>
        <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  const toNum = (val) => Number(val) || 0;
  const hasFilters = selectedCells.length > 0;

  const rows = data.routingStatusMatrix.filter(r => r.shipWindow !== 'TOTAL');
  const totalRow = data.routingStatusMatrix.find(r => r.shipWindow === 'TOTAL') || { shipWindow: 'TOTAL', total: 0 };

  const getRowStyle = (shipWindow) => {
    const name = (shipWindow || '').replace(/^\d\./, '');
    const styles = {
      'Invalid Dates': 'border-l-slate-400 text-slate-500',
      'Pre Start': 'border-l-blue-500 text-blue-700',
      'In Window': 'border-l-green-500 text-green-700',
      'Pass Cancel': 'border-l-red-500 text-red-700',
    };
    return styles[name] || 'border-l-transparent text-slate-600';
  };

  return (
    <div className={`bg-carbon-white border border-carbon-border overflow-hidden ${loading ? 'opacity-60' : ''}`}>
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center gap-2">
        <div className="w-6 h-6 bg-blue-100 flex items-center justify-center">
          <Package size={12} className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Order By Routing Status</h3>
          <p className="text-[10px] text-slate-400">Pick orders only</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-carbon-gray-10">
              <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border whitespace-nowrap">Status</th>
              {data.orderStatusColumns.map(os => (
                <th key={os} className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border whitespace-nowrap">{os}</th>
              ))}
              <th className="px-3 py-2 text-right font-semibold text-slate-700 border-b border-slate-200 bg-slate-100">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const displayName = (row.shipWindow || '').replace(/^\d\./, '');
              const rowStyle = getRowStyle(row.shipWindow);
              const isRowSelected = hasFilters && selectedCells.some(c => c.shipWindow === row.shipWindow);
              const isRowGrayed = hasFilters && !isRowSelected;

              return (
                <tr key={idx} className={`cursor-pointer transition-colors ${isRowSelected ? 'bg-blue-50 hover:bg-blue-100' : isRowGrayed ? 'opacity-40 hover:opacity-60' : 'hover:bg-slate-50'}`} onClick={() => onRowClick && onRowClick(row.shipWindow)}>
                  <td className={`px-3 py-1.5 font-medium border-l-4 border-b border-slate-100 whitespace-nowrap ${rowStyle}`}>{displayName}</td>
                  {data.orderStatusColumns.map(os => (
                    <td key={os} className="px-2 py-1.5 text-right tabular-nums border-b border-slate-100 text-slate-600">{toNum(row[os]) > 0 ? formatCurrency(row[os]) : '—'}</td>
                  ))}
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium border-b border-slate-100 bg-slate-50 text-slate-700">{toNum(row.total) > 0 ? formatCurrency(row.total) : '—'}</td>
                </tr>
              );
            })}
            <tr className="bg-slate-100 font-semibold">
              <td className="px-3 py-2 border-l-4 border-l-slate-600 border-t border-slate-300 text-slate-800">TOTAL</td>
              {data.orderStatusColumns.map(os => (
                <td key={os} className="px-2 py-2 text-right tabular-nums border-t border-slate-300 text-slate-700">{formatCurrency(toNum(totalRow[os]))}</td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums border-t border-slate-300 bg-slate-200 text-slate-800">{formatCurrency(toNum(totalRow.total))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// SCHEDULED BY DATE CHART
// ============================================================================
const ScheduledByDateChart = ({ data, formatCurrency, loading, selectedCells = [], selectedDate, onDateClick }) => {
  if (!data?.scheduledByDate) {
    return (
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
          <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Scheduled By Ship Date</h3>
        </div>
        <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  const hasFilters = selectedCells.length > 0;

  const filteredData = hasFilters
    ? data.scheduledByDate.filter(row => selectedCells.some(c => c.division === row.division && c.shipWindow === row.shipWindow))
    : data.scheduledByDate;

  const aggregatedByDate = {};
  filteredData.forEach(row => {
    if (!aggregatedByDate[row.shipDate]) {
      aggregatedByDate[row.shipDate] = { shipDate: row.shipDate, grossPrice: 0 };
    }
    aggregatedByDate[row.shipDate].grossPrice += row.grossPrice;
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const chartData = Object.values(aggregatedByDate)
    .sort((a, b) => (a.shipDate || '').localeCompare(b.shipDate || ''))
    .map(row => {
      const [year, month, day] = (row.shipDate || '').split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const isPast = row.shipDate < todayStr;
      return {
        date: row.shipDate,
        label: date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }),
        amount: row.grossPrice,
        isToday: row.shipDate === todayStr,
        isTomorrow: row.shipDate === tomorrowStr,
        isPast
      };
    });

  const totalAmount = chartData.reduce((sum, d) => sum + d.amount, 0);

  // Carbon severity/accent tokens: past = red-60, today = green-50,
  // tomorrow = blue-60, future = gray-50 (same semantics as before).
  const getBarColor = (entry) => {
    if (entry.isPast) return '#DA1E28';
    if (entry.isToday) return '#24A148';
    if (entry.isTomorrow) return '#0F62FE';
    return '#8D8D8D';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-carbon-ink text-carbon-white px-3 py-2 text-xs font-plex-mono">
          <p className="font-semibold">{data.label}</p>
          <p className="font-semibold">{formatCurrency(data.amount)}</p>
        </div>
      );
    }
    return null;
  };

  const formatSelectedDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className={`bg-carbon-white border ${selectedDate ? 'border-carbon-blue-60' : 'border-carbon-border'} overflow-hidden ${loading ? 'opacity-60' : ''}`}>
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-green-100 flex items-center justify-center">
            <Calendar size={12} className="text-green-600" />
          </div>
          <div>
            <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Scheduled By Date</h3>
            <p className="text-[10px] text-slate-400">
              Pick orders • Projected Date{hasFilters ? ' • Cell Filtered' : ''}{selectedDate ? ` • ${formatSelectedDate(selectedDate)}` : ''}
            </p>
          </div>
        </div>
        <div className="text-right flex items-center gap-3">
          {selectedDate && (
            <button onClick={() => onDateClick(null)} className="font-plex-mono text-[10.5px] px-2 py-1 border border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10">Clear</button>
          )}
          <div>
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-sm font-bold text-green-600">{formatCurrency(totalAmount)}</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        {chartData.every(d => d.amount === 0) ? (
          <div className="h-32 flex items-center justify-center text-slate-400 text-sm">No scheduled orders</div>
        ) : (
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} width={40} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="amount" radius={[0, 0, 0, 0]} cursor="pointer" onClick={(data) => { if (onDateClick) { onDateClick(data.date === selectedDate ? null : data.date); } }}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry)} stroke={entry.date === selectedDate ? '#000' : 'none'} strokeWidth={entry.date === selectedDate ? 2 : 0} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// OPEN ORDER DETAILS TABLE — reference table pattern (CHA-274)
// ============================================================================
const OpenOrderDetailsTable = ({ data, formatCurrency, loading, selectedCells = [], selectedDate = null, ticketsByPo }) => {
  const hasCellFilters = selectedCells.length > 0;
  const hasDateFilter = selectedDate !== null;
  const hasFilters = hasCellFilters || hasDateFilter;

  const filteredData = data?.orderDetails || [];

  const formatDateValue = (dateStr) => {
    if (!dateStr) return '—';
    return dateStr.split('T')[0];
  };

  const formatGP = (gp) => {
    if (gp === null || gp === undefined) return '—';
    return `${gp.toFixed(1)}%`;
  };

  // Export is byte-identical: always the full dataset the callable returned,
  // regardless of the client-side search / status chips.
  const handleExport = () => {
    const exportData = filteredData.map(row => ({
      'Division': row.division,
      'Warehouse': row.warehouse,
      'Customer': row.customerName,
      'Company': row.company,
      'Amount': row.amount,
      'GP%': row.gpPercent?.toFixed(1),
      'PO#': row.poNo,
      'Order#': row.orderNo,
      'Pick#': row.pickNumber,
      'Projected Date': row.projectedDate?.split('T')[0] || '',
      'Start Date': row.startDate?.split('T')[0] || '',
      'End Date': row.endDate?.split('T')[0] || '',
      'MABD': row.mabd?.split('T')[0] || '',
      'Sts': row.sts,
      'Status': row.status
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Order Details');
    XLSX.writeFile(wb, `OrderDetails_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Presentation-only "late" flag: the ship window's end date is in the past.
  // Local calendar date, NOT toISOString (UTC would flip to tomorrow during
  // US evening hours and mark today's orders late).
  const _n = new Date();
  const todayStr = [
    _n.getFullYear(),
    String(_n.getMonth() + 1).padStart(2, '0'),
    String(_n.getDate()).padStart(2, '0'),
  ].join('-');
  const isLate = (d) => {
    const v = d ? String(d).split('T')[0] : null;
    return !!v && v < todayStr;
  };

  const mono = 'font-plex-mono text-[11.5px] text-carbon-gray-70 whitespace-nowrap';
  const columns = [
    { key: 'poNo', label: 'PO', width: '84px', render: (r) => <span className="font-plex-mono text-[12px] font-semibold text-carbon-ink whitespace-nowrap">{r.poNo}</span> },
    { key: 'customerName', label: 'CUSTOMER', width: 'minmax(130px,1.3fr)', render: (r) => <span className="text-[12.5px] text-carbon-ink overflow-hidden text-ellipsis whitespace-nowrap" title={r.customerName}>{r.customerName}</span> },
    { key: 'division', label: 'DIVISION', width: 'minmax(90px,1fr)', render: (r) => <span className="text-[12px] text-carbon-gray-70 overflow-hidden text-ellipsis whitespace-nowrap" title={r.division}>{r.division}</span> },
    { key: 'warehouse', label: 'WH', width: '44px', render: (r) => <span className={mono}>{r.warehouse}</span> },
    { key: 'company', label: 'COMPANY', width: '70px', render: (r) => <span className={mono}>{r.company}</span> },
    { key: 'pickNumber', label: 'PICK #', width: '72px', render: (r) => <span className={mono}>{r.pickNumber}</span> },
    { key: 'projectedDate', label: 'PROJ', width: '86px', render: (r) => <span className={mono}>{formatDateValue(r.projectedDate)}</span> },
    { key: 'startDate', label: 'START', width: '86px', render: (r) => <span className={mono}>{formatDateValue(r.startDate)}</span> },
    { key: 'endDate', label: 'END', width: '94px', render: (r) => <span className="font-plex-mono text-[11.5px] whitespace-nowrap" style={{ color: isLate(r.endDate) ? '#DA1E28' : '#525252' }}>{formatDateValue(r.endDate)}{isLate(r.endDate) ? ' !' : ''}</span> },
    { key: 'amount', label: 'AMOUNT', width: '88px', align: 'right', render: (r) => <span className="font-plex-mono text-[12px] font-semibold text-carbon-ink text-right block whitespace-nowrap">{formatCurrency(r.amount)}</span> },
    { key: 'gpPercent', label: 'GP %', width: '58px', align: 'right', render: (r) => <span className="font-plex-mono text-[12px] text-right block" style={{ color: gpTone(r.gpPercent) }}>{formatGP(r.gpPercent)}</span> },
    { key: 'sts', label: 'STATUS', width: '64px', render: (r) => <StatusChip sts={stsKeyOf(r)} /> },
  ];

  // Same reducers as before, over the rows currently matched by the filters.
  const totalsCells = (visible) => {
    const totalAmount = visible.reduce((sum, row) => sum + (row.amount || 0), 0);
    const weightedGP = totalAmount > 0
      ? visible.reduce((sum, row) => sum + ((row.amount || 0) * (row.gpPercent || 0)), 0) / totalAmount
      : 0;
    return {
      amount: formatCurrency(totalAmount),
      gpPercent: formatGP(weightedGP),
    };
  };

  const buildDrawer = (r) => ({
    title: r.poNo || '—',
    subtitle: [r.customerName, r.division].filter(Boolean).join(' · '),
    stats: [
      { label: 'AMOUNT', value: formatCurrency(r.amount) },
      { label: 'GP %', value: formatGP(r.gpPercent), color: gpTone(r.gpPercent) },
      { label: 'PROJECTED', value: formatDateValue(r.projectedDate) },
      { label: 'END', value: `${formatDateValue(r.endDate)}${isLate(r.endDate) ? ' !' : ''}`, color: isLate(r.endDate) ? '#DA1E28' : undefined },
      { label: 'PICK #', value: r.pickNumber || '—' },
      { label: 'STATUS', value: STS_CHIP[r.sts]?.label || r.sts || '—' },
    ],
    timeline: [
      r.startDate ? { date: formatDateValue(r.startDate), text: 'Ship window opens' } : null,
      r.projectedDate ? { date: formatDateValue(r.projectedDate), text: 'Projected ship date' } : null,
      r.endDate ? { date: formatDateValue(r.endDate), text: isLate(r.endDate) ? 'Ship window ends — past' : 'Ship window ends', color: isLate(r.endDate) ? '#DA1E28' : undefined } : null,
      r.mabd ? { date: formatDateValue(r.mabd), text: 'MABD' } : null,
    ].filter(Boolean),
  });

  return (
    <CarbonPoTable
      eyebrow="Order Details"
      meta={`${filteredData.length} orders${hasFilters ? ' • Filtered' : ''}`}
      columns={columns}
      rows={filteredData}
      loading={loading || !data?.orderDetails}
      emptyText="No orders found"
      minWidth="1080px"
      searchPlaceholder="Search PO, style, customer…"
      searchFn={(r, q) =>
        [r.poNo, r.customerName, r.division, r.company, r.pickNumber]
          .some((v) => String(v ?? '').toLowerCase().includes(q))
      }
      statusFilters={[
        { key: 'P', label: 'PICK' },
        { key: 'O', label: 'OPEN' },
        { key: 'B', label: 'BULK' },
      ]}
      statusOf={stsKeyOf}
      poOf={(r) => r.poNo}
      ticketsByPo={ticketsByPo}
      onExport={handleExport}
      totalsCells={totalsCells}
      buildDrawer={buildDrawer}
      testId="order-details-table"
    />
  );
};

// ============================================================================
// OPEN CUSTOMER SUMMARY TABLE
// ============================================================================
const OpenCustomerSummaryTable = ({ title, data, formatCurrency, loading, color = 'green' }) => {
  const colorStyles = {
    green: { header: 'from-green-50 to-white', icon: 'bg-green-100 text-green-600' },
    blue: { header: 'from-blue-50 to-white', icon: 'bg-blue-100 text-blue-600' },
    orange: { header: 'from-orange-50 to-white', icon: 'bg-orange-100 text-orange-600' },
  };
  const styles = colorStyles[color] || colorStyles.green;

  if (!data) {
    return (
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className={`px-3 py-2 border-b border-slate-100 bg-gradient-to-r ${styles.header}`}>
          <h3 className="font-plex-mono text-[10px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">{title}</h3>
        </div>
        <div className="p-4 text-center text-slate-400 text-xs">Loading...</div>
      </div>
    );
  }

  const totalAmount = data.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalGrossProfit = data.reduce((sum, row) => sum + (row.grossProfit || 0), 0);
  const avgGP = totalAmount > 0 ? (totalGrossProfit / totalAmount) * 100 : 0;

  return (
    <div className={`bg-carbon-white border border-carbon-border overflow-hidden ${loading ? 'opacity-60' : ''}`}>
      <div className={`px-3 py-2 border-b border-slate-100 bg-gradient-to-r ${styles.header} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className={`w-5 h-5 ${styles.icon} flex items-center justify-center`}>
            <Package size={10} />
          </div>
          <div>
            <h3 className="font-plex-mono text-[10px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">{title}</h3>
            <p className="text-[9px] text-slate-400">{data.length} customers</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[9px] text-slate-500">{formatCurrency(totalAmount)}</p>
        </div>
      </div>
      <div className="overflow-auto" style={{ maxHeight: '200px' }}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-carbon-gray-10 z-10">
            <tr>
              <th className="px-2 py-1.5 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Customer</th>
              <th className="px-2 py-1.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Amount</th>
              <th className="px-2 py-1.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Gross Profit</th>
              <th className="px-2 py-1.5 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">GP%</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-slate-400">No data</td></tr>
            ) : (
              data.map((row, idx) => (
                <tr key={idx} className="hover:bg-slate-50 border-b border-slate-100">
                  <td className="px-2 py-1 whitespace-nowrap max-w-32 truncate" title={row.customerName}>{row.customerName}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatCurrency(row.amount)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatCurrency(row.grossProfit)}</td>
                  <td className={`px-2 py-1 text-right tabular-nums ${row.gpPercent < 0 ? 'text-red-600' : row.gpPercent > 30 ? 'text-green-600' : ''}`}>{row.gpPercent?.toFixed(1)}%</td>
                </tr>
              ))
            )}
          </tbody>
          {data.length > 0 && (
            <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
              <tr className="font-semibold text-slate-700">
                <td className="px-2 py-1.5">TOTAL</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatCurrency(totalAmount)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatCurrency(totalGrossProfit)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums ${avgGP < 0 ? 'text-red-600' : avgGP > 30 ? 'text-green-600' : ''}`}>{avgGP.toFixed(1)}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// SNAPSHOT FILTERS - Shared filters for both snapshot tables
// ============================================================================
const SnapshotFilters = ({ snapshotData, yesterdayData, statusFilter, setStatusFilter, companyFilter, setCompanyFilter, formatCurrency }) => {
  // Get unique divisions from both datasets
  const allOrders = [
    ...(snapshotData?.orders || []),
    ...(yesterdayData?.orders || [])
  ];

  // Extract short division name (e.g., "7.IMG Apparel(42)" -> "IMG")
  const getDivisionShort = (divisionName) => {
    if (!divisionName) return null;
    // Remove leading number and dot, extract first word before parenthesis
    const cleaned = divisionName.replace(/^\d+\./, '').split('(')[0].trim();
    // Get first meaningful word
    const words = cleaned.split(/\s+/);
    return words[0] || cleaned;
  };

  const divisions = [...new Set(allOrders.map(o => getDivisionShort(o.DivisionName)).filter(Boolean))].sort();

  // Calculate totals by division
  const divisionTotals = divisions.reduce((acc, div) => {
    const divOrders = allOrders.filter(o => getDivisionShort(o.DivisionName) === div);
    acc[div] = divOrders.reduce((sum, o) => sum + (Number(o.amount) || Number(o.total_amount) || 0), 0);
    return acc;
  }, {});

  // Status options with colors
  const statusOptions = [
    { key: 'Shipped', label: 'Shipped', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-400' },
    { key: 'Shipped - Not Invoiced', label: 'Not Invoiced', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-400' },
    { key: 'Pending', label: 'Pending', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-400' },
    { key: 'Still Pending', label: 'Still Pending', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-400' },
    { key: 'Rescheduled', label: 'Rescheduled', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-400' },
    { key: 'Date Cleared', label: 'Cleared', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-400' },
  ];

  // Always show status filters, even if no division data yet
  const showDivisions = divisions.length > 0;

  return (
    <div className="bg-carbon-white border border-carbon-border p-3">
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <span className="font-plex-mono text-[9.5px] tracking-[0.12em] text-carbon-gray-50 mr-1">STATUS</span>
        {statusOptions.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(statusFilter === key ? null : key)}
            className={carbonChipCls(statusFilter === key)}
          >
            {label}
          </button>
        ))}
        {statusFilter && (
          <button
            onClick={() => setStatusFilter(null)}
            className="font-plex-mono text-[10.5px] px-2 py-0.5 text-carbon-gray-70 hover:text-carbon-ink"
          >
            ✕ Clear
          </button>
        )}
      </div>
      {showDivisions && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-plex-mono text-[9.5px] tracking-[0.12em] text-carbon-gray-50 mr-1">COMPANY</span>
          {divisions.map(div => (
            <button
              key={div}
              onClick={() => setCompanyFilter(companyFilter === div ? null : div)}
              className={carbonChipCls(companyFilter === div)}
            >
              {div} ({formatCurrency(divisionTotals[div])})
            </button>
          ))}
          {companyFilter && (
            <button
              onClick={() => setCompanyFilter(null)}
              className="font-plex-mono text-[10.5px] px-2 py-0.5 text-carbon-gray-70 hover:text-carbon-ink"
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SHIPPING SNAPSHOT TABLE
// ============================================================================
const ShippingSnapshotTable = ({ data, loading, formatCurrency, onRefresh, statusFilter, companyFilter }) => {
  if (!data && !loading) {
    return null;
  }

  const { orders = [], summary = {}, snapshotDate } = data || {};

  // Extract short division name for filtering
  const getDivisionShort = (divisionName) => {
    if (!divisionName) return null;
    const cleaned = divisionName.replace(/^\d+\./, '').split('(')[0].trim();
    const words = cleaned.split(/\s+/);
    return words[0] || cleaned;
  };

  // Apply filters
  const filteredOrders = orders.filter(row => {
    if (statusFilter && row.current_status !== statusFilter) return false;
    if (companyFilter && getDivisionShort(row.DivisionName) !== companyFilter) return false;
    return true;
  });

  // Calculate filtered totals
  const filteredAmount = filteredOrders.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Shipped': return 'bg-green-100 text-green-800';
      case 'Rescheduled': return 'bg-orange-100 text-orange-800';
      case 'Pending': return 'bg-blue-100 text-blue-800';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="bg-carbon-white border border-carbon-border overflow-hidden mt-3">
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-purple-100 flex items-center justify-center">
            <Package size={12} className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Today's Shipping Snapshot</h3>
            <p className="text-[10px] text-slate-400">
              {snapshotDate || 'Loading...'} • {filteredOrders.length}{filteredOrders.length !== orders.length ? ` of ${orders.length}` : ''} orders {(statusFilter || companyFilter) ? `(${formatCurrency(filteredAmount)})` : 'scheduled'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!loading && summary && !statusFilter && !companyFilter && (
            <div className="flex gap-2 text-[10px]">
              <span className="font-plex-mono px-2 py-0.5 bg-green-100 text-green-700">
                Shipped: {summary.shipped || 0}
              </span>
              <span className="font-plex-mono px-2 py-0.5 bg-blue-100 text-blue-700">
                Pending: {summary.pending || 0}
              </span>
              <span className="font-plex-mono px-2 py-0.5 bg-orange-100 text-orange-700">
                Rescheduled: {summary.rescheduled || 0}
              </span>
            </div>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: '350px' }}>
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading snapshot data...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-slate-400">{(statusFilter || companyFilter) ? 'No orders match filters' : 'No orders scheduled for today'}</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-carbon-gray-10 z-10">
              <tr>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Pick #</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">PO</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Warehouse</th>
                <th className="px-3 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Amount</th>
                <th className="px-3 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Qty</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Customer</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Division</th>
                <th className="px-3 py-2 text-center font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((row, idx) => (
                <tr key={idx} className="hover:bg-slate-50 border-b border-slate-100">
                  <td className="px-3 py-1.5 font-mono text-slate-700">{row.pick_ticket}</td>
                  <td className="px-3 py-1.5 text-slate-600">{row.po_number}</td>
                  <td className="px-3 py-1.5 text-slate-600">{row.warehouse}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(row.amount)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{row.qty?.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-slate-600 max-w-40 truncate" title={row.Customer_Name}>{row.Customer_Name}</td>
                  <td className="px-3 py-1.5 text-slate-600">{row.DivisionName}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`font-plex-mono px-2 py-0.5 text-[10px] font-medium ${getStatusColor(row.current_status)}`}>
                      {row.current_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// YESTERDAY'S UNSHIPPED TABLE - Shows what happened to yesterday's scheduled orders
// ============================================================================
const YesterdayUnshippedTable = ({ data, loading, formatCurrency, onRefresh, statusFilter, companyFilter }) => {
  if (!data && !loading) {
    return null;
  }

  const { orders = [], summary = {}, yesterdayDate } = data || {};

  // Extract short division name for filtering
  const getDivisionShort = (divisionName) => {
    if (!divisionName) return null;
    const cleaned = divisionName.replace(/^\d+\./, '').split('(')[0].trim();
    const words = cleaned.split(/\s+/);
    return words[0] || cleaned;
  };

  // Apply filters - map statusFilter to what_happened field
  const filteredOrders = orders.filter(row => {
    if (statusFilter && row.what_happened !== statusFilter) return false;
    if (companyFilter && getDivisionShort(row.DivisionName) !== companyFilter) return false;
    return true;
  });

  // Calculate filtered totals
  const filteredAmount = filteredOrders.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Shipped': return 'bg-green-100 text-green-800';
      case 'Shipped - Not Invoiced': return 'bg-emerald-100 text-emerald-800';
      case 'Date Cleared': return 'bg-red-100 text-red-800';
      case 'Rescheduled': return 'bg-orange-100 text-orange-800';
      case 'Still Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="bg-carbon-white border border-carbon-border overflow-hidden mt-3">
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-amber-100 flex items-center justify-center">
            <CalendarDays size={12} className="text-amber-600" />
          </div>
          <div>
            <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">Yesterday's Shipping Results</h3>
            <p className="text-[10px] text-slate-400">
              {yesterdayDate || 'Loading...'} • {filteredOrders.length}{filteredOrders.length !== orders.length ? ` of ${orders.length}` : ''} orders {(statusFilter || companyFilter) ? `(${formatCurrency(filteredAmount)})` : 'were scheduled'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!loading && summary && !statusFilter && !companyFilter && (
            <div className="flex gap-1.5 text-[10px] flex-wrap justify-end">
              <span className="font-plex-mono px-2 py-0.5 bg-green-100 text-green-700">
                Shipped: {summary.shipped?.count || 0} ({formatCurrency(summary.shipped?.amount || 0)})
              </span>
              <span className="font-plex-mono px-2 py-0.5 bg-emerald-100 text-emerald-700">
                Not Invoiced: {summary.shippedNotInvoiced?.count || 0}
              </span>
              <span className="font-plex-mono px-2 py-0.5 bg-red-100 text-red-700">
                Cleared: {summary.dateCleared?.count || 0}
              </span>
              <span className="font-plex-mono px-2 py-0.5 bg-orange-100 text-orange-700">
                Rescheduled: {summary.rescheduled?.count || 0}
              </span>
              <span className="font-plex-mono px-2 py-0.5 bg-yellow-100 text-yellow-700">
                Pending: {summary.stillPending?.count || 0}
              </span>
            </div>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: '350px' }}>
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading yesterday's data...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-slate-400">{(statusFilter || companyFilter) ? 'No orders match filters' : 'No orders were scheduled yesterday'}</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-carbon-gray-10 z-10">
              <tr>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Pick #</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">PO</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Warehouse</th>
                <th className="px-3 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Amount</th>
                <th className="px-3 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Qty</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Customer</th>
                <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Division</th>
                <th className="px-3 py-2 text-center font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">What Happened</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((row, idx) => (
                <tr key={idx} className="hover:bg-slate-50 border-b border-slate-100">
                  <td className="px-3 py-1.5 font-mono text-slate-700">{row.pick_ticket}</td>
                  <td className="px-3 py-1.5 text-slate-600">{row.po_number}</td>
                  <td className="px-3 py-1.5 text-slate-600">{row.warehouse}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(row.total_amount)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{row.total_qty?.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-slate-600 max-w-40 truncate" title={row.Customer_Name}>{row.Customer_Name}</td>
                  <td className="px-3 py-1.5 text-slate-600">{row.DivisionName}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`font-plex-mono px-2 py-0.5 text-[10px] font-medium ${getStatusColor(row.what_happened)}`}>
                      {row.what_happened}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
